/**
 * Sales Prospects — CRM voor het sales-team.
 *
 * Multi-user variant van de single-file preview: state in PostgreSQL i.p.v.
 * localStorage; AI-calls (tip + mail) gaan via /Api/sales-ai/* zodat de
 * Anthropic-key server-side blijft.
 *
 * Permissie-gate: ProtectedRoute feature="sales_prospects". Het backend doet
 * geen extra check; defense-in-depth kan later.
 *
 * Layout volgt de FF-conventie: gedeelde <PageHeader> + dezelfde wrapper-spacing
 * als /planning (px {xs:2,sm:3,md:5}, pt {xs:3,md:5}, pb:10). De interne
 * kaart-stijl/kleuren (#526075 etc.) zijn nog uit de preview overgenomen.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import PageHeader from '../../ui-components/PageHeader';
import { bevestig } from '../../utils/bevestig';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';

// ─── Constanten ──────────────────────────────────────────────────────

const STAGES = [
  { id: 'nieuw',      label: 'Nieuw',             color: '#f1f4f5', accent: '#767c7e' },
  { id: 'contact',    label: 'Eerste contact',    color: '#d5e3fd', accent: '#526075' },
  { id: 'gesprek',    label: 'In gesprek',        color: '#dbcdff', accent: '#635983' },
  { id: 'offerte',    label: 'Offerte verstuurd', color: '#ECFDF5', accent: '#10B981' },
  { id: 'klant',      label: 'Klant geworden',    color: '#FFFBEB', accent: '#F59E0B' },
  { id: 'archief',    label: 'Gearchiveerd',      color: '#f1f4f5', accent: '#556071' },
  { id: 'afgevallen', label: 'Afgevallen',        color: '#FEF2F2', accent: '#EF4444' },
];
const ACTIEVE_STAGES = STAGES.filter((s) => !['archief', 'afgevallen'].includes(s.id));
const TOON_OPTIES   = ['Informeel & warm', 'Direct & zakelijk', 'Enthousiast', 'Rustig & geduldig', 'Grappig & luchtig'];
const KANAAL_OPTIES = ['E-mail', 'Telefoon', 'LinkedIn', 'WhatsApp', 'Persoonlijk gesprek'];
const CONTACT_TYPES = [
  { id: 'bellen',   label: 'Telefoongesprek',  icon: 'phone',    kleur: '#10B981', bg: '#ECFDF5', kanaal: 'Telefoon' },
  { id: 'mail',     label: 'E-mail',           icon: 'mail',     kleur: '#526075', bg: '#d5e3fd', kanaal: 'E-mail' },
  { id: 'linkedin', label: 'LinkedIn',         icon: 'link',     kleur: '#0A66C2', bg: '#d5e3fd', kanaal: 'LinkedIn' },
  { id: 'meeting',  label: 'Afspraak/meeting', icon: 'calcheck', kleur: '#F59E0B', bg: '#FFFBEB', kanaal: 'Persoonlijk gesprek' },
  { id: 'anders',   label: 'Overig',           icon: 'note',     kleur: '#767c7e', bg: '#f1f4f5', kanaal: 'E-mail' },
];
const PLATFORMEN = [
  { id: 'linkedin',  label: 'LinkedIn',  kleur: '#0A66C2', basis: 'https://linkedin.com/in/' },
  { id: 'instagram', label: 'Instagram', kleur: '#E1306C', basis: 'https://instagram.com/' },
  { id: 'facebook',  label: 'Facebook',  kleur: '#1877F2', basis: 'https://facebook.com/' },
  { id: 'twitter',   label: 'X/Twitter', kleur: '#000',    basis: 'https://x.com/' },
  { id: 'website',   label: 'Website',   kleur: '#526075', basis: 'https://' },
  { id: 'anders',    label: 'Anders',    kleur: '#767c7e', basis: '' },
];
const DEFAULT_TEAM = ['Joris', 'Vincent', 'Joanne', 'Pepijn', 'Ton', 'Romy', 'Boy', 'Overig'];
const CATEGORIE_OPTIES = [
  'Reclame / Creative',
  'Employer Branding / AMC',
  'Strategie / Branding',
  'Content / Social / Digital',
  'Mid-size Agency',
  'Bedrijven (direct)',
];

// ─── Helpers ─────────────────────────────────────────────────────────

const getWeekKey = (date = new Date()) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const ys = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const w = Math.ceil((((d - ys) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(w).padStart(2, '0')}`;
};
const getWeekLabel = (key) => {
  const [year, w] = key.split('-W');
  const jan1 = new Date(parseInt(year), 0, 1);
  const dow = jan1.getDay() || 7;
  const s = new Date(jan1);
  s.setDate(jan1.getDate() + (dow <= 4 ? 1 - dow : 8 - dow) + (parseInt(w) - 1) * 7);
  const e = new Date(s); e.setDate(s.getDate() + 6);
  const f = (d) => d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
  return `${f(s)} – ${f(e)}`;
};
const getLast8Weeks = () => {
  const keys = []; const d = new Date();
  for (let i = 7; i >= 0; i--) { const c = new Date(d); c.setDate(d.getDate() - i * 7); keys.push(getWeekKey(c)); }
  return [...new Set(keys)];
};
const fmtDt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) +
    ' om ' + d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
};
const fmtDate = (iso) => {
  if (!iso) return '';
  return new Date(iso + 'T00:00').toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
};
const todayStr = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`; };
const isOverdue = (iso) => !!iso && iso < todayStr();
const isDueSoon = (iso) => { if (!iso) return false; const diff = (new Date(iso + 'T00:00') - new Date(todayStr() + 'T00:00')) / 86400000; return diff >= 0 && diff <= 2; };

// Favicon/beeldmerk per prospect: expliciete logo_url wint, anders afgeleid uit
// het website-domein via DuckDuckGo's icon-service (privacy-vriendelijk, geen opslag).
const domainOf = (url) => {
  if (!url) return null;
  try {
    const u = new URL(/^https?:\/\//.test(url) ? url : `https://${url}`);
    return u.hostname.replace(/^www\./, '');
  } catch { return null; }
};
const faviconUrl = (p) => {
  if (p?.logo_url) return p.logo_url;
  const d = domainOf(p?.website);
  return d ? `https://icons.duckduckgo.com/ip3/${d}.ico` : null;
};

const parseCSV = (text) => {
  const rows = []; let row = []; let val = ''; let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQ) {
      if (c === '"') { if (text[i + 1] === '"') { val += '"'; i++; } else inQ = false; }
      else val += c;
    } else if (c === '"') inQ = true;
    else if (c === ',') { row.push(val); val = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++;
      row.push(val); val = '';
      if (row.some((x) => x !== '')) rows.push(row);
      row = [];
    } else val += c;
  }
  if (val !== '' || row.length) { row.push(val); if (row.some((x) => x !== '')) rows.push(row); }
  return rows;
};

const useIsMobile = () => {
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
};

// ─── Icon (inline SVG, zelfde lib als preview) ───────────────────────

const Icon = ({ name, size = 16, color = 'currentColor', style }) => {
  const paths = {
    plus:     "<line x1='12' y1='5' x2='12' y2='19'/><line x1='5' y1='12' x2='19' y2='12'/>",
    search:   "<circle cx='11' cy='11' r='8'/><line x1='21' y1='21' x2='16.65' y2='16.65'/>",
    trash:    "<polyline points='3 6 5 6 21 6'/><path d='M19 6l-1 14H6L5 6'/><path d='M10 11v6'/><path d='M14 11v6'/><path d='M9 6V4h6v2'/>",
    chevron:  "<polyline points='9 18 15 12 9 6'/>",
    spark:    "<path d='M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z'/>",
    mail:     "<rect x='2' y='4' width='20' height='16' rx='2'/><path d='M22 7l-10 7L2 7'/>",
    phone:    "<path d='M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.14 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.11 2.72h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.09 10.1a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 17z'/>",
    note:     "<path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'/><polyline points='14 2 14 8 20 8'/><line x1='16' y1='13' x2='8' y2='13'/><line x1='16' y1='17' x2='8' y2='17'/>",
    close:    "<line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/>",
    target:   "<circle cx='12' cy='12' r='10'/><circle cx='12' cy='12' r='6'/><circle cx='12' cy='12' r='2'/>",
    refresh:  "<polyline points='23 4 23 10 17 10'/><path d='M20.49 15a9 9 0 1 1-2.12-9.36L23 10'/>",
    sliders:  "<line x1='4' y1='21' x2='4' y2='14'/><line x1='4' y1='10' x2='4' y2='3'/><line x1='12' y1='21' x2='12' y2='12'/><line x1='12' y1='8' x2='12' y2='3'/><line x1='20' y1='21' x2='20' y2='16'/><line x1='20' y1='12' x2='20' y2='3'/><line x1='1' y1='14' x2='7' y2='14'/><line x1='9' y1='8' x2='15' y2='8'/><line x1='17' y1='16' x2='23' y2='16'/>",
    check:    "<polyline points='20 6 9 17 4 12'/>",
    copy:     "<rect x='9' y='9' width='13' height='13' rx='2' ry='2'/><path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1'/>",
    send:     "<line x1='22' y1='2' x2='11' y2='13'/><polygon points='22 2 15 22 11 13 2 9 22 2'/>",
    inbox:    "<polyline points='22 12 16 12 14 15 10 15 8 12 2 12'/><path d='M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z'/>",
    link:     "<path d='M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71'/><path d='M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71'/>",
    bar:      "<line x1='18' y1='20' x2='18' y2='10'/><line x1='12' y1='20' x2='12' y2='4'/><line x1='6' y1='20' x2='6' y2='14'/>",
    trend:    "<polyline points='23 6 13.5 15.5 8.5 10.5 1 18'/><polyline points='17 6 23 6 23 12'/>",
    heart:    "<path d='M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z'/>",
    calcheck: "<rect x='3' y='4' width='18' height='18' rx='2' ry='2'/><line x1='16' y1='2' x2='16' y2='6'/><line x1='8' y1='2' x2='8' y2='6'/><line x1='3' y1='10' x2='21' y2='10'/><path d='M9 16l2 2 4-4'/>",
    clock:    "<circle cx='12' cy='12' r='10'/><polyline points='12 6 12 12 16 14'/>",
    alert:    "<circle cx='12' cy='12' r='10'/><line x1='12' y1='8' x2='12' y2='12'/><line x1='12' y1='16' x2='12.01' y2='16'/>",
    archive:  "<polyline points='21 8 21 21 3 21 3 8'/><rect x='1' y='3' width='22' height='5'/><line x1='10' y1='12' x2='14' y2='12'/>",
    upload:   "<polyline points='16 16 12 12 8 16'/><line x1='12' y1='12' x2='12' y2='21'/><path d='M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3'/>",
    download: "<polyline points='8 17 12 21 16 17'/><line x1='12' y1='21' x2='12' y2='3'/>",
    user:     "<path d='M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2'/><circle cx='12' cy='7' r='4'/>",
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" style={style}
         dangerouslySetInnerHTML={{ __html: paths[name] || '' }} />
  );
};

// Favicon-avatar: toont het beeldmerk uit faviconUrl(), met fallback naar de
// beginletter van bedrijf/naam als er geen (geldige) afbeelding is.
const Favicon = ({ prospect, size = 32 }) => {
  const [err, setErr] = useState(false);
  const src = faviconUrl(prospect);
  const initial = (prospect?.bedrijf || prospect?.naam || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <div style={{ width: size, height: size, borderRadius: 8, flexShrink: 0, background: '#f1f4f5', border: '1px solid #e5e9eb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {src && !err
        ? <img src={src} alt="" width={Math.round(size * 0.66)} height={Math.round(size * 0.66)} style={{ objectFit: 'contain', borderRadius: 4 }} onError={() => setErr(true)} />
        : <span style={{ fontFamily: '"Manrope", system-ui, sans-serif', fontSize: Math.round(size * 0.42), fontWeight: 700, color: '#767c7e' }}>{initial}</span>}
    </div>
  );
};

// ─── API helpers ─────────────────────────────────────────────────────

const apiBase = `${BACKEND_URL}/Api`;
const api = {
  load:        () => authFetch(`${apiBase}/sales-prospects`),
  createAccount: (body) => authFetch(`${apiBase}/sales-accounts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  patchAccount:  (id, body) => authFetch(`${apiBase}/sales-accounts/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  removeAccount: (id) => authFetch(`${apiBase}/sales-accounts/${id}`, { method: 'DELETE' }),
  create:      (body) => authFetch(`${apiBase}/sales-prospects`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  patch:       (id, body) => authFetch(`${apiBase}/sales-prospects/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  remove:      (id) => authFetch(`${apiBase}/sales-prospects/${id}`, { method: 'DELETE' }),
  bulk:        (rows) => authFetch(`${apiBase}/sales-prospects/bulk`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ rows }) }),
  addContact:  (pid, body) => authFetch(`${apiBase}/sales-prospects/${pid}/contacts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  delContact:  (id) => authFetch(`${apiBase}/sales-contacts/${id}`, { method: 'DELETE' }),
  addConn:     (pid, body) => authFetch(`${apiBase}/sales-prospects/${pid}/connections`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  delConn:     (id) => authFetch(`${apiBase}/sales-connections/${id}`, { method: 'DELETE' }),
  addMailLog:  (body) => authFetch(`${apiBase}/sales-mail-log`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  delMailLog:  (id) => authFetch(`${apiBase}/sales-mail-log/${id}`, { method: 'DELETE' }),
  assignMailLog: (id, prospectId) => authFetch(`${apiBase}/sales-mail-log/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ prospect_id: prospectId }) }),
  setSetting:  (key, value) => authFetch(`${apiBase}/sales-settings/${key}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value }) }),
  tip:         (body) => authFetch(`${apiBase}/sales-ai/tip`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
  mail:        (body) => authFetch(`${apiBase}/sales-ai/mail`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
};

// ─── Hoofdcomponent ──────────────────────────────────────────────────

export default function SalesProspects() {
  const [accounts, setAccounts]       = useState([]);
  const [prospects, setProspects]     = useState([]); // contactpersonen
  const [contacts, setContacts]       = useState([]);
  const [connections, setConnections] = useState([]);
  const [mailLog, setMailLog]         = useState([]);
  const [settings, setSettings]       = useState({});
  const [loading, setLoading]         = useState(true);

  // Hoofd-tab gesynchroniseerd met de URL (?tab=stats / ?tab=log), zodat tabs
  // deelbaar/bookmarkbaar zijn. 'prospects' is de default zonder query-param.
  const [searchParams, setSearchParams] = useSearchParams();
  const hoofdTab = ['stats', 'log'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'prospects';
  const setHoofdTab = useCallback((id) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id === 'prospects') next.delete('tab'); else next.set('tab', id);
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [actief,    setActief]        = useState(null);   // geselecteerde contactpersoon-id
  const [expanded,  setExpanded]      = useState(() => new Set()); // uitgeklapte account-ids
  const [accountModal, setAccountModal] = useState(null); // null | 'new' | accountId (instellingen)
  const [cpAccount, setCpAccount]     = useState(null);   // account-id waarvoor contactpersoon-form open is
  const [detailTab, setDetailTab]     = useState('prospect');
  const [zoek,      setZoek]          = useState('');
  const [filterStage, setFilter]      = useState('alle');
  const [filterEigenaar, setFE]       = useState('alle');
  const [filterCategorie, setFC]      = useState('alle');
  const [showArchief, setArchief]     = useState(false);
  const [toast,     setToast]         = useState('');
  const [nieuwAccount, setNieuwAccount] = useState({ naam: '', website: '', categorie: '', eigenaar: 'Vincent' });
  const fileRef  = useRef();
  const isMobile = useIsMobile();

  // Anti-clobber voor de polling-refresh: tijdens typen in een veld geen
  // server-state toepassen; na blur verse data ophalen (zie refresh/onBlur).
  const typingRef = useRef(false);
  const missedPollRef = useRef(false);

  const TEAM = useMemo(() => {
    const raw = settings.team_eigenaren;
    if (!raw) return DEFAULT_TEAM;
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }, [settings.team_eigenaren]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const applyData = useCallback((json) => {
    setAccounts(json.accounts || []);
    setProspects(json.prospects || []);
    setContacts(json.contacts || []);
    setConnections(json.connections || []);
    setMailLog(json.mailLog || []);
    setSettings(json.settings || {});
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await api.load();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      applyData(await res.json());
    } catch (err) {
      console.error('[SalesProspects] load:', err);
      showToast('⚠️ Laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [applyData]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => registreerOngedaanSneltoets(), []); // Ctrl+Z = laatste fase/respons-wijziging terug

  // Deep-link: open een specifieke contactpersoon via ?prospect=<id> (bv. vanuit
  // de hoofdpagina-widget). Eénmalig zodra de data geladen is.
  const deepLinkedRef = useRef(false);
  useEffect(() => {
    if (deepLinkedRef.current) return;
    const pid = searchParams.get('prospect');
    if (!pid || prospects.length === 0) return;
    const p = prospects.find((x) => x.id === pid);
    if (!p) return;
    deepLinkedRef.current = true;
    setActief(pid);
    setDetailTab('prospect');
    if (p.account_id) setExpanded((s) => new Set(s).add(p.account_id));
  }, [searchParams, prospects]);

  // Achtergrond-refresh: pak wijzigingen van collega's op (zoals MaterialReservation).
  // Tijdens typen niets toepassen — onthoud dat een poll is gemist en haal na blur
  // verse data op, zodat eigen typewerk nooit verdwijnt.
  const refresh = useCallback(async () => {
    if (typingRef.current) { missedPollRef.current = true; return; }
    try {
      const res = await api.load();
      if (!res.ok) return;
      const json = await res.json();
      if (typingRef.current) { missedPollRef.current = true; return; }
      applyData(json);
    } catch { /* stil — volgende poll probeert opnieuw */ }
  }, [applyData]);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    const tick = () => { if (document.visibilityState === 'visible') refreshRef.current?.(); };
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('focus', tick);
    const id = setInterval(tick, 20000);
    return () => {
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('focus', tick);
      clearInterval(id);
    };
  }, []);

  // Focus-tracking op de wrapper: blokkeer state-replacement tijdens typen.
  const onFocusCapture = useCallback((e) => {
    const t = e.target?.tagName;
    if (t === 'INPUT' || t === 'TEXTAREA') typingRef.current = true;
  }, []);
  const onBlurCapture = useCallback(() => {
    typingRef.current = false;
    if (missedPollRef.current) {
      missedPollRef.current = false;
      setTimeout(() => { if (!typingRef.current) refreshRef.current?.(); }, 600);
    }
  }, []);

  // Berekend: afspraken per week uit alle contacten met afspraak_gemaakt = true
  const afsprakenPerWeek = useMemo(() => {
    const m = {};
    contacts.forEach((c) => {
      if (c.afspraak_gemaakt && c.datum) {
        const w = getWeekKey(new Date(c.datum));
        m[w] = (m[w] || 0) + 1;
      }
    });
    return m;
  }, [contacts]);

  // ─── Mutaties ────────────────────────────────────────────────────

  const update = useCallback(async (id, field, val) => {
    setProspects((p) => p.map((k) => (k.id === id ? { ...k, [field]: val } : k))); // optimistic
    try {
      const res = await api.patch(id, { [field]: val });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      console.error('[SalesProspects] update:', err);
      showToast('⚠️ Bijwerken mislukt — herlaad');
    }
  }, []);

  // Account-veld optimistic bijwerken (instellingen: naam/website/logo/categorie/eigenaar).
  const updateAccount = useCallback(async (id, field, val) => {
    setAccounts((a) => a.map((x) => (x.id === id ? { ...x, [field]: val } : x)));
    try {
      const res = await api.patchAccount(id, { [field]: val });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (err) {
      console.error('[SalesProspects] updateAccount:', err);
      showToast('⚠️ Bijwerken mislukt — herlaad');
    }
  }, []);

  const voegAccountToe = async () => {
    if (!nieuwAccount.naam.trim()) return;
    if (accounts.some((a) => (a.naam || '').toLowerCase() === nieuwAccount.naam.trim().toLowerCase())) {
      showToast('⚠️ ' + nieuwAccount.naam + ' bestaat al!');
      return;
    }
    try {
      const res = await api.createAccount(nieuwAccount);
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        showToast(`⚠️ Toevoegen mislukt (HTTP ${res.status}): ${body.slice(0, 120)}`);
        return;
      }
      const { account } = await res.json();
      setAccounts((a) => [...a, account]);
      setNieuwAccount({ naam: '', website: '', categorie: '', eigenaar: 'Vincent' });
      setAccountModal(null);
      setExpanded((s) => new Set(s).add(account.id));
      setCpAccount(account.id); // meteen een contactpersoon kunnen toevoegen
      showToast('Account toegevoegd ✓');
    } catch (err) {
      showToast('⚠️ Toevoegen mislukt: ' + (err.message || 'netwerkfout'));
    }
  };

  const verwijderAccount = async (id) => {
    const acc = accounts.find((a) => a.id === id);
    const aantal = prospects.filter((p) => p.account_id === id).length;
    if (!(await bevestig({ titel: `Account "${acc?.naam || ''}" verwijderen?`, tekst: aantal ? `Let op: ${aantal} contactpersoon/personen worden ook verwijderd. Dit kan niet ongedaan worden gemaakt.` : 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await api.removeAccount(id);
      const cpIds = prospects.filter((p) => p.account_id === id).map((p) => p.id);
      setAccounts((a) => a.filter((x) => x.id !== id));
      setProspects((p) => p.filter((x) => x.account_id !== id));
      setContacts((c) => c.filter((x) => !cpIds.includes(x.prospect_id)));
      setConnections((c) => c.filter((x) => !cpIds.includes(x.prospect_id)));
      if (cpIds.includes(actief)) setActief(null);
      setAccountModal(null);
      showToast('Account verwijderd');
    } catch (err) {
      showToast('⚠️ Verwijderen mislukt');
    }
  };

  // Contactpersoon toevoegen onder een account.
  const voegContactToe = async (accountId, body) => {
    if (!body.naam?.trim()) return;
    try {
      const res = await api.create({ ...body, naam: body.naam.trim(), account_id: accountId });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        showToast(res.status === 409 ? '⚠️ Contactpersoon bestaat al' : `⚠️ Toevoegen mislukt (HTTP ${res.status})`);
        console.error('[SalesProspects] create cp non-OK:', res.status, txt);
        return;
      }
      const { prospect } = await res.json();
      setProspects((p) => [prospect, ...p]);
      setCpAccount(null);
      setActief(prospect.id);
      setDetailTab('prospect');
      showToast('Contactpersoon toegevoegd ✓');
    } catch (err) {
      showToast('⚠️ Toevoegen mislukt: ' + (err.message || 'netwerkfout'));
    }
  };

  const verwijder = async (id) => {
    if (!(await bevestig({ titel: 'Contactpersoon verwijderen?', tekst: 'Contactmomenten en connecties van deze persoon gaan mee. Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      await api.remove(id);
      setProspects((p) => p.filter((k) => k.id !== id));
      setContacts((c) => c.filter((x) => x.prospect_id !== id));
      setConnections((c) => c.filter((x) => x.prospect_id !== id));
      setActief(null);
      showToast('Verwijderd');
    } catch (err) {
      showToast('⚠️ Verwijderen mislukt');
    }
  };

  // Contactpersoon verplaatsen naar een ander account.
  const verplaatsContact = async (cpId, newAccountId) => {
    if (!newAccountId) return;
    setProspects((p) => p.map((k) => (k.id === cpId ? { ...k, account_id: newAccountId } : k)));
    setExpanded((s) => new Set(s).add(newAccountId));
    try {
      const res = await api.patch(cpId, { account_id: newAccountId });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast('Verplaatst ✓');
    } catch (err) {
      showToast('⚠️ Verplaatsen mislukt — herlaad');
    }
  };

  const importCSV = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const matrix = parseCSV(ev.target.result);
      if (matrix.length < 2) { showToast('⚠️ Geen rijen gevonden in CSV'); return; }
      const headers = matrix[0].map((h) => h.trim().toLowerCase());
      const rows = matrix.slice(1).map((cols) => {
        const row = Object.fromEntries(headers.map((h, i) => [h, (cols[i] || '').trim()]));
        return {
          naam: row.naam || row.name || '',
          bedrijf: row.bedrijf || row.company || '',
          email: row.email || '',
          telefoon: row.telefoon || row.phone || '',
          notitie: row.notitie || row.notes || '',
          eigenaar: row.eigenaar || 'Vincent',
        };
      }).filter((r) => r.naam);
      try {
        const res = await api.bulk(rows);
        const { added = 0, dupes = 0 } = await res.json();
        showToast(`✅ ${added} geïmporteerd${dupes ? ` · ${dupes} dubbelen overgeslagen` : ''}`);
        await load();
      } catch {
        showToast('⚠️ Import mislukt');
      }
    };
    reader.readAsText(file); e.target.value = '';
  };

  const exportCSV = () => {
    const headers = ['naam', 'bedrijf', 'email', 'telefoon', 'stage', 'eigenaar', 'notitie', 'respons', 'volgende_actie', 'volgende_datum'];
    const rows = prospects.map((k) => headers.map((h) => `"${(k[h] || '').toString().replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'fullframe_sales.csv'; a.click();
    URL.revokeObjectURL(url);
    showToast('📥 Export gedownload');
  };

  const onVerstuurd = useCallback(async (entry) => {
    try {
      await api.addMailLog(entry);
      const newProspect = prospects.find((p) => p.id === entry.prospect_id);
      // Update prospect-state op mail_verstuurd + stage upgrade
      await api.patch(entry.prospect_id, {
        mail_verstuurd: true,
        stage: newProspect?.stage === 'nieuw' ? 'contact' : newProspect?.stage,
      });
      await load();
      showToast('✉️ Mail verstuurd & opgeslagen');
    } catch (err) {
      showToast('⚠️ Log opslaan mislukt');
    }
  }, [prospects, load]);

  // ─── Computed ────────────────────────────────────────────────────

  const overdueCount = prospects.filter((k) =>
    k.volgende_datum && isOverdue(k.volgende_datum) && !['archief', 'afgevallen'].includes(k.stage)
  ).length;
  const dueSoonCount = prospects.filter((k) =>
    k.volgende_datum && isDueSoon(k.volgende_datum) && !['archief', 'afgevallen'].includes(k.stage)
  ).length;

  const categorieOpties = useMemo(() => {
    const set = new Set(CATEGORIE_OPTIES);
    accounts.forEach((a) => { if (a.categorie) set.add(a.categorie); });
    return Array.from(set);
  }, [accounts]);

  const accountById = useMemo(() => Object.fromEntries(accounts.map((a) => [a.id, a])), [accounts]);
  const cpByAccount = useMemo(() => {
    const m = {};
    prospects.forEach((p) => { (m[p.account_id] = m[p.account_id] || []).push(p); });
    return m;
  }, [prospects]);

  // Account-centrische filtering: eigenaar/categorie filteren op account, fase op
  // contactpersoon, zoek matcht account-naam of contactpersoon-naam/functie.
  const inArchief = (k) => ['archief', 'afgevallen'].includes(k.stage);
  const zichtbareAccounts = useMemo(() => {
    const q = zoek.toLowerCase();
    return accounts
      .filter((a) => (filterEigenaar === 'alle' || a.eigenaar === filterEigenaar)
        && (filterCategorie === 'alle' || a.categorie === filterCategorie))
      .map((a) => {
        const accMatch = !q || (a.naam || '').toLowerCase().includes(q);
        const alle = cpByAccount[a.id] || [];
        const cps = alle.filter((k) => {
          const stageOk = showArchief ? inArchief(k) : !inArchief(k);
          const stageFilterOk = filterStage === 'alle' || k.stage === filterStage;
          const searchOk = accMatch || k.naam.toLowerCase().includes(q) || (k.functie || '').toLowerCase().includes(q);
          return stageOk && stageFilterOk && searchOk;
        });
        return { account: a, cps, totaal: alle.length, accMatch };
      })
      .filter(({ cps, totaal, accMatch }) =>
        cps.length > 0 || (totaal === 0 && accMatch && filterStage === 'alle' && !showArchief))
      .sort((x, y) => (x.account.naam || '').localeCompare(y.account.naam || ''));
  }, [accounts, cpByAccount, zoek, filterEigenaar, filterCategorie, filterStage, showArchief]);

  const actiefKlant = prospects.find((k) => k.id === actief);
  const actiefAccount = actiefKlant ? accountById[actiefKlant.account_id] : (accountModal && accountModal !== 'new' ? accountById[accountModal] : null);
  const actiefContacts = useMemo(() =>
    actiefKlant ? contacts.filter((c) => c.prospect_id === actiefKlant.id) : [],
    [contacts, actiefKlant]
  );
  const actiefConnections = useMemo(() =>
    actiefKlant ? connections.filter((c) => c.prospect_id === actiefKlant.id) : [],
    [connections, actiefKlant]
  );
  const actiefMails = useMemo(() =>
    actiefKlant ? mailLog.filter((m) => m.prospect_id === actiefKlant.id) : [],
    [mailLog, actiefKlant]
  );
  const counts = Object.fromEntries(ACTIEVE_STAGES.map((s) => [s.id, prospects.filter((k) => k.stage === s.id).length]));
  const stageOf = (k) => STAGES.find((s) => s.id === k.stage) || STAGES[0];

  // ─── Style helpers ───────────────────────────────────────────────

  // FF-wrapper: zelfde spacing als /planning (HubPage) en MateriaalBeheer.
  // fontFamily blijft op de wrapper staan zodat children met `fontFamily: inherit`
  // het juiste Inter-font behouden.
  const wrapperSx = {
    width: '100%', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 10,
    minHeight: '100vh', fontFamily: 'Inter,-apple-system,BlinkMacSystemFont,sans-serif', color: '#2d3335',
  };

  // FF design-tokens (gelijkgetrokken met MateriaalBeheer): slate primary
  // #526075 + tertiary #635983, Manrope voor knoppen/nav, Inter voor body,
  // card-radius 14 + subtiele shadow, gradient primary-knop.
  const css = {
    btnPri:   { cursor: 'pointer', background: 'linear-gradient(135deg, #635983 0%, #526075 100%)', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 18px', fontSize: 13.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, fontFamily: '"Manrope", system-ui, sans-serif', boxShadow: '0 4px 12px rgba(99,89,131,0.22)' },
    btnGhost: { cursor: 'pointer', background: '#f1f4f5', color: '#556071', border: 'none', borderRadius: 9, padding: '9px 14px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, fontFamily: '"Manrope", system-ui, sans-serif' },
    card:     { background: '#fff', borderRadius: 14, border: '1px solid rgba(45,51,53,0.07)', padding: 16, marginBottom: 12, boxShadow: '0 1px 3px rgba(45,51,53,0.04)' },
    input:    { fontFamily: 'inherit', border: '1px solid #e5e9eb', borderRadius: 9, padding: '9px 12px', fontSize: 14, background: '#fff', color: '#2d3335', width: '100%', outline: 'none', boxSizing: 'border-box' },
    label:    { fontSize: 11, fontWeight: 700, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6, display: 'block' },
    tag:      (s) => ({ display: 'inline-flex', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: s.color, color: s.accent }),
    dot:      (s) => ({ width: 8, height: 8, borderRadius: '50%', background: s.accent, flexShrink: 0 }),
    row:      (a) => ({ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, cursor: 'pointer', background: a ? '#d5e3fd' : 'transparent' }),
    stageBtn: (a, s) => ({ cursor: 'pointer', border: a ? '1.5px solid ' + s.accent : '1.5px solid #e5e9eb', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: a ? 600 : 400, background: a ? s.color : '#fff', color: a ? s.accent : '#556071', fontFamily: 'inherit' }),
    modal:    { position: 'fixed', inset: 0, background: 'rgba(45,51,53,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(4px)' },
    modalBox: { background: '#fff', borderRadius: 18, padding: 28, width: 460, maxWidth: '92vw', boxShadow: '0 20px 60px rgba(45,51,53,0.18)', maxHeight: '92vh', overflowY: 'auto' },
    toast:    { position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)', background: '#2d3335', color: '#f1f4f5', padding: '10px 22px', borderRadius: 30, fontSize: 14, fontFamily: 'inherit', zIndex: 2000, whiteSpace: 'nowrap' },
  };

  if (loading) {
    return (
      <Box sx={wrapperSx}>
        <PageHeader
          icon="handshake"
          title="Sales Prospects"
          subtitle="Full Frame Studio"
        />
        <div style={{ textAlign: 'center', padding: '80px 0', color: '#767c7e' }}>Laden…</div>
      </Box>
    );
  }

  return (
    <Box sx={wrapperSx} onFocusCapture={onFocusCapture} onBlurCapture={onBlurCapture}>
      <PageHeader
        icon="handshake"
        title="Sales Prospects"
        subtitle={`Full Frame Studio · ${accounts.length} accounts · ${prospects.filter((k) => !['archief', 'afgevallen'].includes(k.stage)).length} contactpersonen`}
      />
      {/* Sub-navigatie (tabs) onder de gedeelde header; CSV/Export/Nieuw in eigen
          rij eronder zodat de tabs niet shiften. */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'flex-start', gap: 10, marginBottom: hoofdTab === 'prospects' ? 12 : 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
            <div style={{ display: 'flex', background: '#f1f4f5', borderRadius: 10, padding: 3, gap: 2, flex: isMobile ? 1 : 'none', width: isMobile ? '100%' : 'auto', flexShrink: 0 }}>
              {[{ id: 'prospects', l: 'Prospects', i: 'user' }, { id: 'stats', l: 'Stats', i: 'bar' }, { id: 'log', l: 'Log', i: 'inbox', badge: mailLog.filter((m) => !m.prospect_id).length }].map(({ id, l, i, badge }) => (
                <button key={id} onClick={() => setHoofdTab(id)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, padding: '8px 14px', borderRadius: 8, border: 'none', fontFamily: 'inherit', fontSize: 13, fontWeight: hoofdTab === id ? 600 : 400, background: hoofdTab === id ? '#fff' : 'transparent', color: hoofdTab === id ? '#2d3335' : '#556071', boxShadow: hoofdTab === id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.15s', flex: isMobile ? 1 : 'none' }}>
                  <Icon name={i} size={13} color={hoofdTab === id ? '#526075' : '#767c7e'} />{l}
                  {badge > 0 && <span style={{ background: '#526075', color: '#fff', borderRadius: 20, padding: '1px 6px', fontSize: 10, fontWeight: 700, marginLeft: 2 }}>{badge}</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
        {hoofdTab === 'prospects' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: isMobile ? 'flex-start' : 'flex-end' }}>
            {overdueCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#FEF2F2', border: '1px solid #FED7D7', borderRadius: 8, padding: '6px 12px', fontSize: 12, color: '#EF4444', fontWeight: 600 }}>
                <Icon name="alert" size={13} color="#EF4444" />{overdueCount} verlopen
              </div>
            )}
            {dueSoonCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '6px 12px', fontSize: 12, color: '#D97706', fontWeight: 600 }}>
                <Icon name="clock" size={13} color="#D97706" />{dueSoonCount} binnenkort
              </div>
            )}
            <input ref={fileRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={importCSV} />
            <button style={css.btnGhost} onClick={() => fileRef.current.click()} title="CSV importeren">
              <Icon name="upload" size={14} />{!isMobile && 'CSV import'}
            </button>
            <button style={css.btnGhost} onClick={exportCSV} title="Exporteren">
              <Icon name="download" size={14} />{!isMobile && 'Export'}
            </button>
            <button style={{ ...css.btnPri, flex: isMobile ? 1 : 'none', justifyContent: 'center' }} onClick={() => { setNieuwAccount({ naam: '', website: '', categorie: '', eigenaar: 'Vincent' }); setAccountModal('new'); }}>
              <Icon name="plus" size={15} color="#fff" />{isMobile ? 'Account' : 'Nieuw account'}
            </button>
          </div>
        )}
      </div>

      {hoofdTab === 'log'   && <VerzondLog log={mailLog} prospects={prospects} accounts={accounts} reload={load} />}
      {hoofdTab === 'stats' && <StatsDashboard prospects={prospects} accounts={accounts} contacts={contacts} mailLog={mailLog} afspraken={afsprakenPerWeek} team={TEAM} isMobile={isMobile} />}

      {hoofdTab === 'prospects' && (
        <>
          <WeekTracker afspraken={afsprakenPerWeek} />

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3,1fr)' : 'repeat(auto-fill,minmax(120px,1fr))', gap: isMobile ? 8 : 10, marginBottom: 16 }}>
            {/* 'Alle prospects' = filter uit; toont dat de fase-kaarten filters zijn */}
            <div onClick={() => setFilter('alle')} style={{ ...css.card, cursor: 'pointer', padding: isMobile ? '10px' : '12px 16px', marginBottom: 0, border: filterStage === 'alle' ? '1.5px solid #526075' : '1px solid #e5e9eb', background: filterStage === 'alle' ? '#d5e3fd' : '#fff' }}>
              <div style={{ fontSize: isMobile ? 9 : 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, lineHeight: 1.3 }}>Alle prospects</div>
              <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: filterStage === 'alle' ? '#526075' : '#2d3335' }}>{Object.values(counts).reduce((a, b) => a + b, 0)}</div>
            </div>
            {ACTIEVE_STAGES.map((s) => (
              <div key={s.id} onClick={() => setFilter(filterStage === s.id ? 'alle' : s.id)} style={{ ...css.card, cursor: 'pointer', padding: isMobile ? '10px' : '12px 16px', marginBottom: 0, border: filterStage === s.id ? '1.5px solid ' + s.accent : '1px solid #e5e9eb', background: filterStage === s.id ? s.color : '#fff' }}>
                <div style={{ fontSize: isMobile ? 9 : 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4, lineHeight: 1.3 }}>{s.label}</div>
                <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color: filterStage === s.id ? s.accent : '#2d3335' }}>{counts[s.id] || 0}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#767c7e', pointerEvents: 'none' }}>
                <Icon name="search" size={15} />
              </span>
              <input style={{ ...css.input, paddingLeft: 36 }} placeholder="Zoek op account of contactpersoon…" value={zoek} onChange={(e) => setZoek(e.target.value)} />
            </div>
            <select style={{ ...css.input, width: 'auto' }} value={filterEigenaar} onChange={(e) => setFE(e.target.value)}>
              <option value="alle">Alle eigenaren</option>
              {TEAM.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select style={{ ...css.input, width: 'auto' }} value={filterCategorie} onChange={(e) => setFC(e.target.value)}>
              <option value="alle">Alle categorieën</option>
              {categorieOpties.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button onClick={() => setArchief((v) => !v)} style={{ ...css.btnGhost, background: showArchief ? '#d5e3fd' : '#f1f4f5', color: showArchief ? '#526075' : '#556071' }}>
              <Icon name="archive" size={14} color={showArchief ? '#526075' : '#556071'} />
              {showArchief ? 'Actief' : 'Archief'}
            </button>
          </div>

          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ ...css.card, flex: actiefKlant && !isMobile ? '0 0 330px' : '1', padding: 8, marginBottom: 0, maxHeight: isMobile ? '65vh' : '58vh', overflowY: 'auto', display: isMobile && actiefKlant ? 'none' : 'block' }}>
              {zichtbareAccounts.length === 0 && (
                <div style={{ padding: 32, textAlign: 'center', color: '#767c7e', fontSize: 14 }}>Geen accounts gevonden.</div>
              )}
              {zichtbareAccounts.map(({ account: acc, cps }) => {
                const open = expanded.has(acc.id);
                return (
                  <div key={acc.id} style={{ borderRadius: 12, marginBottom: 6, background: open ? '#f8fafb' : 'transparent', border: open ? '1px solid #eef1f2' : '1px solid transparent' }}>
                    {/* Account-regel */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer', borderRadius: 12 }}
                      onClick={() => setExpanded((s) => { const n = new Set(s); n.has(acc.id) ? n.delete(acc.id) : n.add(acc.id); return n; })}>
                      <Favicon prospect={acc} size={28} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{acc.naam}</div>
                        <div style={{ fontSize: 11, color: '#767c7e', marginTop: 1, display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span>{(cpByAccount[acc.id] || []).length === 0 ? 'geen contactpersonen' : `${(cpByAccount[acc.id] || []).length} contactperso${(cpByAccount[acc.id] || []).length === 1 ? 'on' : 'nen'}`}</span>
                          {acc.eigenaar && <span style={{ fontSize: 10, background: '#f1f4f5', borderRadius: 20, padding: '1px 6px', color: '#556071' }}>{acc.eigenaar}</span>}
                          {acc.categorie && <span style={{ fontSize: 10, background: '#d5e3fd', borderRadius: 20, padding: '1px 6px', color: '#526075' }}>{acc.categorie}</span>}
                        </div>
                      </div>
                      <button title="Account-instellingen" onClick={(e) => { e.stopPropagation(); setAccountModal(acc.id); }} style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 4, display: 'flex', alignItems: 'center' }}>
                        <Icon name="sliders" size={15} color="#767c7e" />
                      </button>
                      <Icon name="chevron" size={14} color="#adb3b5" style={{ transform: open ? 'rotate(90deg)' : 'none' }} />
                    </div>

                    {/* Contactpersonen */}
                    {open && (
                      <div style={{ marginLeft: 18, paddingLeft: 10, borderLeft: '1.5px solid #e5e9eb', marginBottom: 6 }}>
                        {cps.map((k) => {
                          const s = stageOf(k), a = actief === k.id, over = isOverdue(k.volgende_datum), soon = isDueSoon(k.volgende_datum);
                          return (
                            <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, cursor: 'pointer', background: a ? '#d5e3fd' : 'transparent', marginBottom: 2 }}
                              onClick={() => { setActief(a && !isMobile ? null : k.id); setDetailTab('prospect'); }}>
                              <div style={css.dot(s)} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.naam}</div>
                                <div style={{ fontSize: 11, color: '#767c7e', marginTop: 1, display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                                  {k.functie && <span>{k.functie}</span>}
                                  {k.volgende_datum && (
                                    <span style={{ fontSize: 10, background: over ? '#FEF2F2' : soon ? '#FFFBEB' : '#ECFDF5', color: over ? '#EF4444' : soon ? '#D97706' : '#10B981', borderRadius: 20, padding: '1px 6px', fontWeight: 600 }}>
                                      {over ? '⚠️ Verlopen' : soon ? '🔔 Binnenkort' : '✓ ' + fmtDate(k.volgende_datum)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span style={{ ...css.tag(s), fontSize: 10, padding: '2px 8px' }}>{s.label}</span>
                            </div>
                          );
                        })}
                        {cpAccount === acc.id
                          ? <NieuwContactForm css={css} onCancel={() => setCpAccount(null)} onSave={(body) => voegContactToe(acc.id, body)} />
                          : (
                            <button onClick={() => setCpAccount(acc.id)} style={{ cursor: 'pointer', background: 'none', border: 'none', padding: '6px 10px', fontSize: 12, color: '#526075', fontFamily: 'inherit', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Icon name="plus" size={12} color="#526075" /> contactpersoon
                            </button>
                          )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {actiefKlant && (
              <DetailPanel
                klant={actiefKlant}
                account={actiefAccount}
                accounts={accounts}
                allProspects={prospects}
                onMove={verplaatsContact}
                contacts={actiefContacts}
                connections={actiefConnections}
                mails={actiefMails}
                detailTab={detailTab}
                setDetailTab={setDetailTab}
                update={update}
                team={TEAM}
                settings={settings}
                onVerstuurd={onVerstuurd}
                onVerwijder={verwijder}
                onClose={() => setActief(null)}
                onContactsChanged={load}
                onConnectionsChanged={load}
                isMobile={isMobile}
                showToast={showToast}
                css={css}
              />
            )}
          </div>
        </>
      )}

      {/* Nieuw account */}
      {accountModal === 'new' && (
        <div style={css.modal} onClick={() => setAccountModal(null)}>
          <div style={css.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Favicon prospect={nieuwAccount} size={32} />
                <div style={{ fontSize: 18, fontWeight: 700 }}>Nieuw account</div>
              </div>
              <button style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 4 }} onClick={() => setAccountModal(null)}>
                <Icon name="close" size={18} color="#556071" />
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[{ f: 'naam', l: 'Accountnaam *', p: 'Bureau / bedrijf' }, { f: 'website', l: 'Website', p: 'https://bedrijf.nl' }].map(({ f, l, p }) => (
                <div key={f}>
                  <label style={css.label}>{l}</label>
                  <input style={css.input} placeholder={p} value={nieuwAccount[f]} onChange={(e) => setNieuwAccount((n) => ({ ...n, [f]: e.target.value }))} />
                </div>
              ))}
              <div>
                <label style={css.label}>Eigenaar</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {TEAM.map((t) => (
                    <button key={t} onClick={() => setNieuwAccount((n) => ({ ...n, eigenaar: t }))} style={{ cursor: 'pointer', border: nieuwAccount.eigenaar === t ? '1.5px solid #526075' : '1px solid #e5e9eb', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontFamily: 'inherit', background: nieuwAccount.eigenaar === t ? '#d5e3fd' : '#fff', color: nieuwAccount.eigenaar === t ? '#526075' : '#556071', fontWeight: nieuwAccount.eigenaar === t ? 600 : 400 }}>{t}</button>
                  ))}
                </div>
              </div>
              <div>
                <label style={css.label}>Categorie</label>
                <select style={css.input} value={nieuwAccount.categorie} onChange={(e) => setNieuwAccount((n) => ({ ...n, categorie: e.target.value }))}>
                  <option value="">— Geen —</option>
                  {CATEGORIE_OPTIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {accounts.some((a) => (a.naam || '').toLowerCase() === nieuwAccount.naam.trim().toLowerCase() && nieuwAccount.naam.trim()) && (
                <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#D97706' }}>⚠️ {nieuwAccount.naam} bestaat al.</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button style={{ cursor: 'pointer', background: '#f1f4f5', border: 'none', borderRadius: 10, padding: '9px 18px', fontSize: 14, fontWeight: 500, fontFamily: 'inherit', color: '#556071' }} onClick={() => setAccountModal(null)}>Annuleer</button>
              <button style={css.btnPri} onClick={voegAccountToe}>Toevoegen</button>
            </div>
          </div>
        </div>
      )}

      {/* Account-instellingen */}
      {accountModal && accountModal !== 'new' && accountById[accountModal] && (
        <AccountInstellingenModal
          account={accountById[accountModal]}
          team={TEAM}
          categorieOpties={categorieOpties}
          update={updateAccount}
          onDelete={() => verwijderAccount(accountModal)}
          onClose={() => setAccountModal(null)}
          css={css}
        />
      )}

      {toast && <div style={css.toast}>{toast}</div>}
    </Box>
  );
}

// ─── NieuwContactForm (contactpersoon toevoegen onder account) ───────

function NieuwContactForm({ css, onSave, onCancel }) {
  const [v, setV] = useState({ naam: '', functie: '', email: '', stage: 'nieuw' });
  const inp = { ...css.input, padding: '7px 10px', fontSize: 13 };
  const kan = !!v.naam.trim();
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e9eb', borderRadius: 10, padding: 10, marginTop: 4, marginBottom: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input autoFocus style={inp} placeholder="Naam *" value={v.naam} onChange={(e) => setV((s) => ({ ...s, naam: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter' && kan) onSave(v); }} />
      <input style={inp} placeholder="Functie (bijv. CEO)" value={v.functie} onChange={(e) => setV((s) => ({ ...s, functie: e.target.value }))} />
      <input style={inp} placeholder="E-mail" value={v.email} onChange={(e) => setV((s) => ({ ...s, email: e.target.value }))} />
      <select style={inp} value={v.stage} onChange={(e) => setV((s) => ({ ...s, stage: e.target.value }))}>
        {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
      </select>
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => kan && onSave(v)} disabled={!kan} style={{ cursor: kan ? 'pointer' : 'not-allowed', background: '#526075', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', opacity: kan ? 1 : 0.5 }}>Opslaan</button>
        <button onClick={onCancel} style={{ cursor: 'pointer', background: '#f1f4f5', color: '#556071', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontFamily: 'inherit' }}>Annuleer</button>
      </div>
    </div>
  );
}

// ─── AccountInstellingenModal ────────────────────────────────────────
// Account-niveau instellingen: naam, website (favicon), logo, categorie, eigenaar
// + account verwijderen. Velden via update() (optimistic PATCH op het account).

function AccountInstellingenModal({ account, team, categorieOpties, update, onDelete, onClose, css }) {
  const velden = [
    { field: 'naam',     label: 'Accountnaam',          ph: 'Bureau / bedrijf',     icon: null },
    { field: 'website',  label: 'Website',              ph: 'https://bedrijf.nl',   icon: 'link' },
    { field: 'logo_url', label: 'Logo-URL (optioneel)', ph: 'https://…/logo.png',   icon: null },
  ];
  return (
    <div style={css.modal} onClick={onClose}>
      <div style={css.modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <Favicon prospect={account} size={36} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 700 }}>Account-instellingen</div>
              <div style={{ fontSize: 12, color: '#767c7e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{account.naam}</div>
            </div>
          </div>
          <button style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 4 }} onClick={onClose}><Icon name="close" size={18} color="#556071" /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {velden.map(({ field, label, ph, icon }) => (
            <div key={field}>
              <label style={css.label}>{icon && <Icon name={icon} size={11} />} {label}</label>
              <input style={css.input} value={account[field] || ''} placeholder={ph} onChange={(e) => update(account.id, field, e.target.value)} />
            </div>
          ))}
          <div>
            <label style={css.label}><Icon name="user" size={11} /> Eigenaar</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {team.map((t) => (
                <button key={t} onClick={() => update(account.id, 'eigenaar', t)} style={{ cursor: 'pointer', border: account.eigenaar === t ? '1.5px solid #526075' : '1.5px solid #e5e9eb', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontFamily: 'inherit', background: account.eigenaar === t ? '#d5e3fd' : '#fff', color: account.eigenaar === t ? '#526075' : '#556071', fontWeight: account.eigenaar === t ? 600 : 400 }}>{t}</button>
              ))}
            </div>
          </div>
          <div>
            <label style={css.label}>Categorie</label>
            <select style={css.input} value={account.categorie || ''} onChange={(e) => update(account.id, 'categorie', e.target.value || null)}>
              <option value="">— Geen —</option>
              {categorieOpties.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={css.label}><Icon name="note" size={11} /> Notitie (account)</label>
            <textarea rows={3} style={{ ...css.input, resize: 'vertical' }} value={account.notitie || ''} placeholder="Context over dit account…" onChange={(e) => update(account.id, 'notitie', e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 22 }}>
          <button onClick={onDelete} style={{ cursor: 'pointer', background: '#FEF2F2', color: '#EF4444', border: 'none', borderRadius: 10, padding: '9px 14px', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="trash" size={13} color="#EF4444" /> Account verwijderen
          </button>
          <button onClick={onClose} style={{ cursor: 'pointer', background: '#526075', color: '#fff', border: 'none', borderRadius: 10, padding: '9px 18px', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' }}>Klaar</button>
        </div>
      </div>
    </div>
  );
}

// ─── DetailPanel ─────────────────────────────────────────────────────

function DetailPanel({ klant, account, accounts = [], allProspects = [], onMove, contacts, connections, mails = [], detailTab, setDetailTab, update, team, settings, onVerstuurd, onVerwijder, onClose, onContactsChanged, onConnectionsChanged, isMobile, showToast, css }) {
  const tabBtn = (id, lbl, icon) => (
    <button key={id} onClick={() => setDetailTab(id)}
      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: '7px 11px', fontSize: 12, fontWeight: detailTab === id ? 600 : 400, fontFamily: 'inherit', border: 'none', borderBottom: detailTab === id ? '2px solid #526075' : '2px solid transparent', background: 'transparent', color: detailTab === id ? '#526075' : '#556071', whiteSpace: 'nowrap' }}>
      <Icon name={icon} size={12} color={detailTab === id ? '#526075' : '#767c7e'} />{lbl}
    </button>
  );
  const style = isMobile
    ? { ...css.card, width: '100%', marginBottom: 0, minHeight: '65vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }
    : { ...css.card, flex: 1, marginBottom: 0, maxHeight: '58vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' };

  return (
    <div style={style}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 0, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {isMobile && (
            <button style={{ cursor: 'pointer', background: '#f1f4f5', border: 'none', borderRadius: 8, padding: '7px 10px', display: 'flex', alignItems: 'center', fontFamily: 'inherit' }} onClick={onClose}>
              <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
          )}
          <Favicon prospect={account || klant} size={40} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{klant.naam}</div>
            <div style={{ fontSize: 13, color: '#556071', marginTop: 1 }}>{[klant.functie, account?.naam].filter(Boolean).join(' · ') || account?.naam}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          {accounts.length > 1 && (
            <select title="Verplaats naar ander account" value="" onChange={(e) => { if (e.target.value) onMove(klant.id, e.target.value); e.target.value = ''; }}
              style={{ fontFamily: 'inherit', border: '1px solid #e5e9eb', borderRadius: 8, padding: '6px 8px', fontSize: 12, background: '#f1f4f5', color: '#556071', maxWidth: 130, cursor: 'pointer' }}>
              <option value="">Verplaats…</option>
              {[...accounts].filter((a) => a.id !== klant.account_id).sort((a, b) => (a.naam || '').localeCompare(b.naam || '')).map((a) => (
                <option key={a.id} value={a.id}>{a.naam}</option>
              ))}
            </select>
          )}
          <button title="Contactpersoon verwijderen" style={{ cursor: 'pointer', background: '#FEF2F2', border: 'none', borderRadius: 8, padding: '7px 10px', display: 'flex', alignItems: 'center' }} onClick={() => onVerwijder(klant.id)}>
            <Icon name="trash" size={14} color="#EF4444" />
          </button>
          {!isMobile && (
            <button title="Sluiten" style={{ cursor: 'pointer', background: '#f1f4f5', border: 'none', borderRadius: 8, padding: '7px 10px', display: 'flex', alignItems: 'center' }} onClick={onClose}>
              <Icon name="close" size={14} color="#556071" />
            </button>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #f1f4f5', margin: '12px 0 16px', flexShrink: 0, overflowX: 'auto' }}>
        {tabBtn('prospect', 'Prospect', 'user')}
        {tabBtn('actie', 'Actie', 'calcheck')}
        {tabBtn('contact', 'Log', 'clock')}
        {tabBtn('persoonlijk', 'Persoonlijk', 'heart')}
        {tabBtn('mail', 'Mail', 'mail')}
        {tabBtn('tip', 'Tip', 'spark')}
        {tabBtn('instellingen', 'Instellingen', 'sliders')}
      </div>

      {detailTab === 'prospect' && (
        <ProspectTab klant={klant} connections={connections} update={update} team={team} css={css} onConnectionsChanged={onConnectionsChanged} />
      )}
      {detailTab === 'actie' && <ActieTab klant={klant} update={update} css={css} />}
      {detailTab === 'contact' && <ContactLogboek klant={klant} contacts={contacts} mails={mails} accounts={accounts} allProspects={allProspects} reload={onContactsChanged} showToast={showToast} />}
      {detailTab === 'persoonlijk' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Icon name="heart" size={13} color="#EF4444" />
            <label style={{ ...css.label, margin: 0, color: '#EF4444' }}>Persoonlijke notities</label>
          </div>
          <div style={{ fontSize: 13, color: '#767c7e', marginBottom: 10, lineHeight: 1.6 }}>
            Familie, hobby's, vakantie, interesses — alleen zichtbaar in de tool.
          </div>
          <textarea rows={8} style={{ ...css.input, resize: 'vertical', lineHeight: 1.75, fontSize: 13 }}
            placeholder={'Bijv:\n- Heeft 2 kinderen, gaat in augustus naar Portugal\n- Grote voetbalfan (Ajax)\n- Begonnen met hardlopen'}
            value={klant.persoonlijk || ''} onChange={(e) => update(klant.id, 'persoonlijk', e.target.value)} />
          {klant.persoonlijk && (
            <div style={{ marginTop: 10, background: '#FFF5F5', border: '1px solid #FED7D7', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#C53030', lineHeight: 1.6 }}>
              💡 Lees dit voor je contact opneemt met {klant.naam.split(' ')[0]}!
            </div>
          )}
        </div>
      )}
      {detailTab === 'mail' && <MailPanel klant={klant} account={account} update={update} settings={settings} onVerstuurd={onVerstuurd} showToast={showToast} />}
      {detailTab === 'tip' && <TipPanel klant={klant} update={update} />}
      {detailTab === 'instellingen' && <InstellingenTab klant={klant} update={update} css={css} />}
    </div>
  );
}

// ─── InstellingenTab (contactpersoon) ────────────────────────────────
// Bewerk de identiteit van de contactpersoon: naam + functie/rol. Bedrijf,
// website, logo en categorie horen bij het account (tandwiel naast het account).
function InstellingenTab({ klant, update, css }) {
  const velden = [
    { field: 'naam',    label: 'Volledige naam', ph: 'Voor- en achternaam', icon: 'user' },
    { field: 'functie', label: 'Functie / rol',  ph: 'bijv. CEO, Art director', icon: null },
  ];
  return (
    <div>
      <div style={{ fontSize: 12, color: '#767c7e', lineHeight: 1.5, marginBottom: 16 }}>
        Naam en functie van deze contactpersoon. Accountnaam, website, logo en categorie
        beheer je via het tandwiel naast het account in de lijst.
      </div>
      {velden.map(({ field, label, ph, icon }) => (
        <div key={field} style={{ marginBottom: 14 }}>
          <label style={css.label}>{icon && <Icon name={icon} size={11} />} {label}</label>
          <input style={css.input} value={klant[field] || ''} placeholder={ph}
            onChange={(e) => update(klant.id, field, e.target.value)} />
        </div>
      ))}
    </div>
  );
}

// ─── ProspectTab ─────────────────────────────────────────────────────

function ProspectTab({ klant, connections, update, team, css, onConnectionsChanged }) {
  // Fase/respons: één klik is snel misgeklikt — toast met "Ongedaan maken" zet de vorige
  // waarde terug via dezelfde (optimistische) update.
  const wijzigMetOngedaan = (field, val, tekst) => {
    const vorige = klant[field] ?? '';
    if (vorige === val) return;
    update(klant.id, field, val);
    biedOngedaanAan(tekst, () => update(klant.id, field, vorige));
  };
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        {[{ icon: 'mail', field: 'email', label: 'E-mail', ph: 'naam@bedrijf.nl' }, { icon: 'phone', field: 'telefoon', label: 'Telefoon', ph: '06 ...' }].map(({ icon, field, label, ph }) => (
          <div key={field}>
            <label style={css.label}><Icon name={icon} size={11} /> {label}</label>
            <input style={css.input} value={klant[field] || ''} placeholder={ph} onChange={(e) => update(klant.id, field, e.target.value)} />
          </div>
        ))}
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={css.label}><Icon name="user" size={11} /> Functie / rol</label>
        <input style={css.input} value={klant.functie || ''} placeholder="bijv. CEO, Art director" onChange={(e) => update(klant.id, 'functie', e.target.value)} />
      </div>
      <ConnectiesVeld klant={klant} connections={connections} reload={onConnectionsChanged} />
      <div style={{ marginBottom: 16 }}>
        <label style={css.label}>Fase</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {STAGES.map((st) => (
            <button key={st.id} style={css.stageBtn(klant.stage === st.id, st)} onClick={() => wijzigMetOngedaan('stage', st.id, `Fase → ${st.label}`)}>{st.label}</button>
          ))}
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={css.label}>Respons op mail</label>
        <div style={{ display: 'flex', gap: 6 }}>
          {[{ id: 'ja', l: '✓ Positief', bg: '#ECFDF5', k: '#10B981' }, { id: 'nee', l: '✗ Negatief', bg: '#FEF2F2', k: '#EF4444' }, { id: '', l: '— Onbekend', bg: '#f1f4f5', k: '#767c7e' }].map(({ id, l, bg, k }) => {
            const a = (klant.respons || '') === id;
            return (
              <button key={id} onClick={() => wijzigMetOngedaan('respons', id, `Respons → ${l.replace(/^[^\w]+/, '')}`)} style={{ cursor: 'pointer', padding: '5px 12px', borderRadius: 8, border: a ? `1.5px solid ${k}` : '1.5px solid #e5e9eb', background: a ? bg : '#fff', color: a ? k : '#767c7e', fontSize: 12, fontWeight: a ? 600 : 400, fontFamily: 'inherit' }}>{l}</button>
            );
          })}
        </div>
      </div>
      <div>
        <label style={css.label}><Icon name="note" size={11} /> Notitie</label>
        <textarea rows={3} style={{ ...css.input, resize: 'vertical' }} placeholder="Context over deze prospect…" value={klant.notitie || ''} onChange={(e) => update(klant.id, 'notitie', e.target.value)} />
      </div>
    </div>
  );
}

// ─── ActieTab ────────────────────────────────────────────────────────

function ActieTab({ klant, update, css }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Icon name="calcheck" size={14} color="#526075" />
        <label style={{ ...css.label, margin: 0 }}>Volgende actie & datum</label>
      </div>
      <div style={{ fontSize: 13, color: '#767c7e', marginBottom: 14, lineHeight: 1.6 }}>Noteer de volgende stap en wanneer. Je ziet een herinnering in de lijst.</div>
      <div style={{ marginBottom: 12 }}>
        <label style={css.label}>Actie omschrijving</label>
        <input style={css.input} placeholder="bijv. Terugbellen, Offerte nabellen…" value={klant.volgende_actie || ''} onChange={(e) => update(klant.id, 'volgende_actie', e.target.value)} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={css.label}>Streefdatum</label>
        <input type="date" style={{ ...css.input, width: 'auto' }} value={klant.volgende_datum || ''} onChange={(e) => update(klant.id, 'volgende_datum', e.target.value || null)} />
      </div>
      {klant.volgende_actie && klant.volgende_datum && (
        <div style={{
          background: isOverdue(klant.volgende_datum) ? '#FEF2F2' : isDueSoon(klant.volgende_datum) ? '#FFFBEB' : '#ECFDF5',
          border: `1px solid ${isOverdue(klant.volgende_datum) ? '#FED7D7' : isDueSoon(klant.volgende_datum) ? '#FDE68A' : '#A7F3D0'}`,
          borderRadius: 12, padding: '12px 16px', marginBottom: 16,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: isOverdue(klant.volgende_datum) ? '#EF4444' : isDueSoon(klant.volgende_datum) ? '#D97706' : '#10B981' }}>
            {isOverdue(klant.volgende_datum) ? '⚠️ Verlopen' : isDueSoon(klant.volgende_datum) ? '🔔 Binnenkort' : '✓ Gepland'}
          </div>
          <div style={{ fontSize: 13, color: '#556071', marginTop: 4 }}>{klant.volgende_actie} — {fmtDate(klant.volgende_datum)}</div>
        </div>
      )}
      <div>
        <label style={css.label}>Snelle acties</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['Terugbellen', 'Offerte nabellen', 'LinkedIn bericht', 'Afspraak bevestigen', 'Voorstel sturen'].map((a) => (
            <button key={a} onClick={() => update(klant.id, 'volgende_actie', a)} style={{ cursor: 'pointer', border: '1px solid #e5e9eb', borderRadius: 20, padding: '4px 12px', fontSize: 12, background: '#f1f4f5', color: '#556071', fontFamily: 'inherit' }}>{a}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ConnectiesVeld ──────────────────────────────────────────────────

function ConnectiesVeld({ klant, connections, reload }) {
  const [nieuw, setNieuw] = useState({ platform: 'linkedin', url: '' });
  const [open, setOpen] = useState(false);
  const pf = (id) => PLATFORMEN.find((p) => p.id === id) || PLATFORMEN[PLATFORMEN.length - 1];
  const voegToe = async () => {
    if (!nieuw.url.trim()) return;
    try {
      await api.addConn(klant.id, nieuw);
      setNieuw({ platform: 'linkedin', url: '' });
      setOpen(false);
      reload();
    } catch {}
  };
  const verwijder = async (id) => {
    if (!(await bevestig({ titel: 'Connectie verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try { await api.delConn(id); reload(); } catch {}
  };
  const inp = { fontFamily: 'inherit', border: '1px solid #e5e9eb', borderRadius: 8, padding: '7px 10px', fontSize: 13, background: '#f1f4f5', color: '#2d3335', outline: 'none', boxSizing: 'border-box' };
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
          <Icon name="link" size={11} /> Connecties
        </label>
        <button onClick={() => setOpen((v) => !v)} style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: 11, color: '#526075', fontFamily: 'inherit', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 3, padding: 0 }}>
          <Icon name="plus" size={11} color="#526075" /> Toevoegen
        </button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: open ? 8 : 0 }}>
        {connections.map((c) => {
          const p = pf(c.platform);
          const href = c.url.startsWith('http') ? c.url : p.basis + c.url;
          return (
            <div key={c.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#f1f4f5', border: '1px solid #e5e9eb', borderRadius: 20, padding: '4px 10px 4px 8px', fontSize: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.kleur, display: 'inline-block' }} />
              <a href={href} target="_blank" rel="noreferrer" style={{ color: '#556071', textDecoration: 'none', fontWeight: 500 }}>{p.label}: {c.url}</a>
              <button onClick={() => verwijder(c.id)} style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0, display: 'flex', alignItems: 'center' }}>
                <Icon name="close" size={11} color="#767c7e" />
              </button>
            </div>
          );
        })}
        {connections.length === 0 && !open && <span style={{ fontSize: 12, color: '#767c7e', fontStyle: 'italic' }}>Nog geen connecties.</span>}
      </div>
      {open && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <select value={nieuw.platform} onChange={(e) => setNieuw((n) => ({ ...n, platform: e.target.value }))} style={{ ...inp, width: 'auto' }}>
            {PLATFORMEN.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <input style={{ ...inp, flex: 1, minWidth: 130 }} placeholder={pf(nieuw.platform).basis || 'url of gebruikersnaam'} value={nieuw.url} onChange={(e) => setNieuw((n) => ({ ...n, url: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && voegToe()} />
          <button onClick={voegToe} style={{ cursor: 'pointer', background: '#526075', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 500, fontFamily: 'inherit' }}>OK</button>
          <button onClick={() => setOpen(false)} style={{ cursor: 'pointer', background: '#f1f4f5', color: '#556071', border: 'none', borderRadius: 8, padding: '7px 10px', fontSize: 12, fontFamily: 'inherit' }}>✕</button>
        </div>
      )}
    </div>
  );
}

// ─── ContactLogboek ──────────────────────────────────────────────────

function ContactLogboek({ klant, contacts, mails = [], accounts = [], allProspects = [], reload, showToast }) {
  const [nieuw, setNieuw] = useState({ type: 'bellen', datum: new Date().toISOString().slice(0, 16), notitie: '', afspraak_gemaakt: false });
  const [formOpen, setForm] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [openMail, setOpenMail] = useState(null);
  const [busyMail, setBusyMail] = useState(false);
  const ct = (id) => CONTACT_TYPES.find((t) => t.id === id) || CONTACT_TYPES[CONTACT_TYPES.length - 1];
  // Mail verplaatsen naar een andere contactpersoon (zelfde dropdown als de Log-tab).
  const verplaatsMail = async (mailId, prospectId) => {
    if (!prospectId || prospectId === klant.id) return;
    setBusyMail(true);
    try { await api.assignMailLog(mailId, prospectId); await reload(); showToast('Mail verplaatst ✓'); }
    catch { showToast('⚠️ Verplaatsen mislukt'); }
    finally { setBusyMail(false); }
  };
  const voegToe = async () => {
    if (!nieuw.notitie.trim()) return;
    try {
      await api.addContact(klant.id, { ...nieuw, datum: new Date(nieuw.datum).toISOString(), kanaal: ct(nieuw.type).kanaal });
      if (nieuw.afspraak_gemaakt) showToast('🎉 Afspraak geteld!');
      setNieuw({ type: 'bellen', datum: new Date().toISOString().slice(0, 16), notitie: '', afspraak_gemaakt: false });
      setForm(false);
      reload();
    } catch {}
  };
  const verwijder = async (id) => {
    if (!(await bevestig({ titel: 'Contactmoment verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try { await api.delContact(id); reload(); } catch {}
  };
  const inp = { fontFamily: 'inherit', border: '1px solid #e5e9eb', borderRadius: 8, padding: '8px 10px', fontSize: 13, background: '#f1f4f5', color: '#2d3335', outline: 'none', boxSizing: 'border-box', width: '100%' };
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Contactmomenten — {contacts.length} gelogd</span>
        <button onClick={() => setForm((v) => !v)} style={{ cursor: 'pointer', background: formOpen ? '#d5e3fd' : '#f1f4f5', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', color: formOpen ? '#526075' : '#556071', display: 'flex', alignItems: 'center', gap: 4 }}>
          <Icon name="plus" size={12} color={formOpen ? '#526075' : '#556071'} /> Toevoegen
        </button>
      </div>
      {formOpen && (
        <div style={{ background: '#f1f4f5', border: '1px solid #e5e9eb', borderRadius: 12, padding: 14, marginBottom: 14 }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>Type</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {CONTACT_TYPES.map((t) => (
                <button key={t.id} onClick={() => setNieuw((n) => ({ ...n, type: t.id }))} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 20, border: nieuw.type === t.id ? `1.5px solid ${t.kleur}` : '1px solid #e5e9eb', background: nieuw.type === t.id ? t.bg : '#fff', color: nieuw.type === t.id ? t.kleur : '#556071', fontSize: 12, fontWeight: nieuw.type === t.id ? 600 : 400, fontFamily: 'inherit' }}>
                  <Icon name={t.icon} size={11} color={nieuw.type === t.id ? t.kleur : '#767c7e'} />{t.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 5 }}>Datum & tijd</div>
            <input type="datetime-local" value={nieuw.datum} onChange={(e) => setNieuw((n) => ({ ...n, datum: e.target.value }))} style={{ ...inp, width: 'auto' }} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 5 }}>Wat is er besproken?</div>
            <textarea rows={3} value={nieuw.notitie} onChange={(e) => setNieuw((n) => ({ ...n, notitie: e.target.value }))} placeholder="Samenvatting van het gesprek..." style={{ ...inp, resize: 'vertical', lineHeight: 1.6 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }} onClick={() => setNieuw((n) => ({ ...n, afspraak_gemaakt: !n.afspraak_gemaakt }))}>
            <div style={{ width: 18, height: 18, borderRadius: 5, border: nieuw.afspraak_gemaakt ? 'none' : '1.5px solid #adb3b5', background: nieuw.afspraak_gemaakt ? '#10B981' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {nieuw.afspraak_gemaakt && <Icon name="check" size={11} color="#fff" />}
            </div>
            <span style={{ fontSize: 13, color: nieuw.afspraak_gemaakt ? '#10B981' : '#556071', fontWeight: nieuw.afspraak_gemaakt ? 600 : 400 }}>Er is een afspraak uit voortgekomen 🎉</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={voegToe} style={{ cursor: 'pointer', background: '#526075', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 500, fontFamily: 'inherit' }}>Opslaan</button>
            <button onClick={() => setForm(false)} style={{ cursor: 'pointer', background: '#f1f4f5', color: '#556071', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontFamily: 'inherit' }}>Annuleer</button>
          </div>
        </div>
      )}
      {contacts.length === 0 && !formOpen
        ? <div style={{ textAlign: 'center', padding: '24px 0', color: '#767c7e', fontSize: 13 }}>Nog geen contactmomenten.</div>
        : contacts.map((c) => {
          const t = ct(c.type);
          return (
            <div key={c.id} style={{ borderRadius: 12, border: '1px solid #e5e9eb', overflow: 'hidden', background: '#fff', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer' }} onClick={() => setOpenId(openId === c.id ? null : c.id)}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: t.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name={t.icon} size={14} color={t.kleur} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: t.kleur }}>{t.label}</span>
                    {c.afspraak_gemaakt && <span style={{ fontSize: 10, background: '#ECFDF5', color: '#10B981', borderRadius: 20, padding: '1px 7px', fontWeight: 600 }}>Afspraak ✓</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#767c7e', marginTop: 1 }}>{fmtDt(c.datum)}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button title="Contactmoment verwijderen" onClick={(ev) => { ev.stopPropagation(); verwijder(c.id); }} style={{ cursor: 'pointer', background: '#FEF2F2', border: 'none', borderRadius: 6, padding: '4px 6px', display: 'flex', alignItems: 'center' }}>
                    <Icon name="trash" size={12} color="#EF4444" />
                  </button>
                  <Icon name="chevron" size={13} color="#adb3b5" />
                </div>
              </div>
              {openId === c.id && (
                <div style={{ borderTop: '1px solid #f1f4f5', padding: '10px 12px', background: '#f1f4f5', fontSize: 13, color: '#556071', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                  {c.notitie || <span style={{ color: '#767c7e', fontStyle: 'italic' }}>Geen notitie.</span>}
                </div>
              )}
            </div>
          );
        })
      }

      {/* Gekoppelde mails (BCC sales@, automatisch ingelezen) */}
      {mails.length > 0 && (
        <div style={{ marginTop: contacts.length ? 18 : 4 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Mails — {mails.length} gelogd</span>
          <div style={{ marginTop: 10 }}>
            {[...mails].sort((a, b) => new Date(b.datum) - new Date(a.datum)).map((m) => (
              <div key={m.id} style={{ borderRadius: 12, border: '1px solid #e5e9eb', overflow: 'hidden', background: '#fff', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer' }} onClick={() => setOpenMail(openMail === m.id ? null : m.id)}>
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: '#d5e3fd', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name="mail" size={14} color="#526075" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#2d3335', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>{m.onderwerp || '(geen onderwerp)'}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 20, padding: '1px 7px', background: m.richting === 'inkomend' ? '#d5e3fd' : '#f1f4f5', color: m.richting === 'inkomend' ? '#526075' : '#767c7e' }}>{m.richting === 'inkomend' ? '↓ In' : '↑ Uit'}</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#767c7e', marginTop: 1 }}>{m.afzender ? `${m.afzender} · ` : ''}{fmtDt(m.datum)}</div>
                  </div>
                  <Icon name="chevron" size={13} color="#adb3b5" />
                </div>
                {openMail === m.id && (
                  <div style={{ borderTop: '1px solid #f1f4f5', padding: '10px 12px', background: '#f1f4f5' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, color: '#767c7e' }}>Verkeerde persoon? Verplaats naar:</span>
                      <select disabled={busyMail} value="" onChange={(ev) => { if (ev.target.value) verplaatsMail(m.id, ev.target.value); ev.target.value = ''; }}
                        style={{ fontFamily: 'inherit', border: '1px solid #e5e9eb', borderRadius: 8, padding: '5px 8px', fontSize: 12, background: '#fff', color: '#2d3335', flex: 1, minWidth: 160 }}>
                        <option value="" disabled>Contactpersoon — kies account…</option>
                        {[...accounts].sort((a, b) => (a.naam || '').localeCompare(b.naam || '')).map((a) => {
                          const cps = allProspects.filter((p) => p.account_id === a.id && p.id !== klant.id).sort((x, y) => (x.naam || '').localeCompare(y.naam || ''));
                          if (cps.length === 0) return null;
                          return (
                            <optgroup key={a.id} label={a.naam}>
                              {cps.map((p) => <option key={p.id} value={p.id}>{p.naam}{p.functie ? ` (${p.functie})` : ''}</option>)}
                            </optgroup>
                          );
                        })}
                      </select>
                    </div>
                    <div style={{ fontSize: 13, color: '#556071', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                      {m.mail_tekst || <span style={{ color: '#767c7e', fontStyle: 'italic' }}>Geen inhoud.</span>}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TipPanel ────────────────────────────────────────────────────────

function TipPanel({ klant, update }) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ctx = klant.tip_context || { toon: 'Informeel & warm', kanaal: 'E-mail', extra: '' };
  const setCtx = (f, v) => update(klant.id, 'tip_context', { ...ctx, [f]: v });
  const haalTip = async () => {
    setLoading(true);
    try {
      const stl = STAGES.find((s) => s.id === klant.stage)?.label || klant.stage;
      const res = await api.tip({
        naam: klant.naam, bedrijf: klant.bedrijf || '', stage_label: stl,
        notitie: klant.notitie || '', persoonlijk: klant.persoonlijk || '',
        toon: ctx.toon, kanaal: ctx.kanaal, extra: ctx.extra || '',
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const { tip } = await res.json();
      update(klant.id, 'tip', tip);
    } catch {
      update(klant.id, 'tip', 'Kon geen tip ophalen — probeer opnieuw.');
    }
    setLoading(false);
  };
  const chip = (a) => ({ cursor: 'pointer', border: a ? '1.5px solid #526075' : '1px solid #e5e9eb', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontFamily: 'inherit', background: a ? '#d5e3fd' : '#fff', color: a ? '#526075' : '#556071', fontWeight: a ? 600 : 400 });
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="spark" size={12} color="#526075" />
          <span style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Aanpak-suggestie</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => setOpen((v) => !v)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, background: open ? '#d5e3fd' : '#f1f4f5', border: '1px solid #e5e9eb', borderRadius: 8, padding: '5px 10px', fontSize: 12, fontFamily: 'inherit', color: open ? '#526075' : '#556071' }}>
            <Icon name="sliders" size={12} color={open ? '#526075' : '#556071'} /> Verfijn
          </button>
          <button disabled={loading} onClick={haalTip} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, background: '#526075', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', color: '#fff', opacity: loading ? 0.6 : 1 }}>
            <Icon name={klant.tip ? 'refresh' : 'spark'} size={12} color="#fff" />{loading ? 'Even denken…' : klant.tip ? 'Vernieuw' : 'Geef tip'}
          </button>
        </div>
      </div>
      {open && (
        <div style={{ background: '#f1f4f5', border: '1px solid #e5e9eb', borderRadius: 12, padding: 14, marginBottom: 12 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8 }}>Toon</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{TOON_OPTIES.map((t) => <button key={t} onClick={() => setCtx('toon', t)} style={chip(ctx.toon === t)}>{t}</button>)}</div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 8 }}>Kanaal</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{KANAAL_OPTIES.map((k) => <button key={k} onClick={() => setCtx('kanaal', k)} style={chip(ctx.kanaal === k)}>{k}</button>)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>Extra context</div>
            <input style={{ fontFamily: 'inherit', border: '1px solid #e5e9eb', borderRadius: 8, padding: '7px 10px', fontSize: 13, background: '#fff', color: '#2d3335', width: '100%', outline: 'none', boxSizing: 'border-box' }} placeholder="bijv. 'kennen elkaar van een event'" value={ctx.extra} onChange={(e) => setCtx('extra', e.target.value)} />
          </div>
        </div>
      )}
      {!open && (
        <div style={{ display: 'flex', gap: 5, marginBottom: 10, flexWrap: 'wrap' }}>
          {[ctx.toon, ctx.kanaal].map((v) => <span key={v} style={{ fontSize: 11, background: '#d5e3fd', color: '#526075', borderRadius: 20, padding: '2px 9px', fontWeight: 500 }}>{v}</span>)}
        </div>
      )}
      {klant.tip
        ? <div style={{ background: '#d5e3fd', border: '1px solid #d5e3fd', borderRadius: 12, padding: '14px 16px', fontSize: 14, lineHeight: 1.65, color: '#465469' }}>{klant.tip}</div>
        : <div style={{ fontSize: 13, color: '#767c7e' }}>Kies toon & kanaal en klik op "Geef tip" voor advies over {klant.naam.split(' ')[0]}.</div>
      }
    </div>
  );
}

// ─── MailPanel ───────────────────────────────────────────────────────

function MailPanel({ klant, account, update, settings, onVerstuurd, showToast }) {
  const bedrijfNaam = klant.bedrijf || account?.naam || '';
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [bevestig, setBevestig] = useState(false);
  const afzenders = (settings.afzenders || 'vincent@fullframe.nl,info@fullframe.nl').split(',').map((s) => s.trim()).filter(Boolean);
  const [afzender, setAfzender] = useState(afzenders[0]);
  const bccAdres = settings.bcc_adres || 'sales@fullframe.nl';
  const basismail = settings.basismail || '';
  const mail = klant.mail_tekst || '';

  const genereer = async () => {
    if (!basismail.trim()) { showToast('⚠️ Basismail leeg — vul in via settings'); return; }
    setLoading(true);
    try {
      const res = await api.mail({
        naam: klant.naam, bedrijf: bedrijfNaam, notitie: klant.notitie || '',
        basismail,
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const { tekst, onderwerp } = await res.json();
      update(klant.id, 'mail_tekst', tekst);
      update(klant.id, 'mail_onderwerp', onderwerp);
    } catch {
      update(klant.id, 'mail_tekst', 'Kon de mail niet genereren — probeer opnieuw.');
    }
    setLoading(false);
  };
  // Platte tekst: de gepersonaliseerde mail als die er is, anders de basistemplate.
  const copyText = (mail && mail.trim()) ? mail : basismail;
  const kopieer = () => { if (!copyText) return; navigator.clipboard.writeText(copyText).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); };
  const onderwerpDef = () => klant.mail_onderwerp || 'Full Frame Exclusive — even voorstellen';
  const openMail = () => {
    if (!mail || !klant.email) return;
    window.open(`mailto:${encodeURIComponent(klant.email)}?bcc=${encodeURIComponent(bccAdres)}&subject=${encodeURIComponent(onderwerpDef())}&body=${encodeURIComponent(mail)}`, '_blank');
    setBevestig(true);
  };
  const bevestigVerzonden = () => {
    onVerstuurd({
      prospect_id: klant.id, naam: klant.naam, bedrijf: bedrijfNaam, email: klant.email,
      afzender, onderwerp: onderwerpDef(), mail_tekst: mail,
    });
    setBevestig(false);
  };
  const btn = (bg, color, disabled) => ({ cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 5, border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', background: bg, color, opacity: disabled ? 0.5 : 1 });
  return (
    <div>
      {/* Mail-kop + platte-tekst kopieerknop (zonder opmaak, regeleinden behouden) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="mail" size={13} color="#526075" />
          <span style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Mail</span>
        </div>
        <button onClick={kopieer} disabled={!copyText} title="Kopieer als platte tekst (zonder opmaak, met regeleinden)"
          style={btn(copied ? '#ECFDF5' : '#f1f4f5', copied ? '#10B981' : '#556071', !copyText)}>
          <Icon name={copied ? 'check' : 'copy'} size={12} color={copied ? '#10B981' : '#556071'} />{copied ? 'Gekopieerd!' : (mail && mail.trim() ? 'Kopieer platte tekst' : 'Kopieer basistemplate')}
        </button>
      </div>
      <details style={{ marginBottom: 14 }}>
        <summary style={{ fontSize: 12, color: '#556071', cursor: 'pointer', userSelect: 'none', padding: '6px 0' }}>Bekijk basistemplate</summary>
        <div style={{ marginTop: 8, background: '#f1f4f5', border: '1px solid #e5e9eb', borderRadius: 10, padding: '12px 14px', fontSize: 13, lineHeight: 1.7, color: '#556071', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{basismail}</div>
      </details>
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6, display: 'block' }}>Versturen als</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {afzenders.map((v) => (
            <button key={v} onClick={() => setAfzender(v)} style={{ cursor: 'pointer', border: afzender === v ? '1.5px solid #526075' : '1px solid #e5e9eb', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontFamily: 'inherit', background: afzender === v ? '#d5e3fd' : '#fff', color: afzender === v ? '#526075' : '#556071', fontWeight: afzender === v ? 600 : 400 }}>{v}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={genereer} disabled={loading} style={btn('#526075', '#fff', loading)}>
          <Icon name={mail ? 'refresh' : 'spark'} size={12} color="#fff" />{loading ? 'Personaliseren…' : mail ? 'Opnieuw genereren' : 'Personaliseer voor ' + klant.naam.split(' ')[0]}
        </button>
        {mail && <>
          <button onClick={kopieer} style={btn(copied ? '#ECFDF5' : '#f1f4f5', copied ? '#10B981' : '#556071', false)}>
            <Icon name={copied ? 'check' : 'copy'} size={12} color={copied ? '#10B981' : '#556071'} />{copied ? 'Gekopieerd!' : 'Kopieer'}
          </button>
          <button onClick={openMail} disabled={!klant.email} style={btn('#2d3335', '#fff', !klant.email)}>
            <Icon name="send" size={12} color="#fff" />Openen in mail
          </button>
        </>}
      </div>
      {bevestig && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#92400E', flex: 1, minWidth: 180 }}>Je mailprogramma is geopend. Is de mail daadwerkelijk verzonden?</span>
          <button onClick={bevestigVerzonden} style={{ cursor: 'pointer', background: '#10B981', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>Ja, log &amp; markeer</button>
          <button onClick={() => setBevestig(false)} style={{ cursor: 'pointer', background: '#f1f4f5', color: '#556071', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontFamily: 'inherit' }}>Nee</button>
        </div>
      )}
      {mail && klant.mail_onderwerp && (
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4, display: 'block' }}>Onderwerpregel</label>
          <input style={{ fontFamily: 'inherit', border: '1px solid #e5e9eb', borderRadius: 8, padding: '7px 10px', fontSize: 13, background: '#f1f4f5', color: '#2d3335', width: '100%', outline: 'none', boxSizing: 'border-box', fontWeight: 500 }} value={klant.mail_onderwerp} onChange={(e) => update(klant.id, 'mail_onderwerp', e.target.value)} />
        </div>
      )}
      {mail && klant.email && (
        <div style={{ fontSize: 12, color: '#767c7e', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Icon name="inbox" size={11} color="#767c7e" />BCC naar <strong style={{ color: '#556071' }}>{bccAdres}</strong>
        </div>
      )}
      {mail && mail.length > 1800 && (
        <div style={{ fontSize: 12, color: '#D97706', marginBottom: 8 }}>⚠️ Lange mail ({mail.length} tekens) — sommige mailprogramma's kappen via "Openen in mail" af. Gebruik desnoods Kopieer en plak handmatig.</div>
      )}
      {mail && !klant.email && (
        <div style={{ fontSize: 12, color: '#F59E0B', marginBottom: 8 }}>⚠️ Voeg een e-mailadres toe bij Prospect om te versturen.</div>
      )}
      {mail
        ? <textarea value={mail} onChange={(e) => update(klant.id, 'mail_tekst', e.target.value)} rows={16} style={{ fontFamily: 'inherit', border: '1px solid #e5e9eb', borderRadius: 10, padding: '12px 14px', fontSize: 13, lineHeight: 1.75, background: '#f1f4f5', color: '#2d3335', width: '100%', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
        : <div style={{ background: '#f1f4f5', border: '1.5px dashed #e5e9eb', borderRadius: 12, padding: '28px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>✉️</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: '#556071', marginBottom: 4 }}>Nog geen gepersonaliseerde mail</div>
          <div style={{ fontSize: 13, color: '#767c7e' }}>Er wordt ook meteen een onderwerpregel gegenereerd.</div>
        </div>
      }
    </div>
  );
}

// ─── VerzondLog ──────────────────────────────────────────────────────

function VerzondLog({ log, prospects = [], accounts = [], reload }) {
  const [open, setOpen] = useState(null);
  const [zoek, setZoek] = useState('');
  const [busy, setBusy] = useState(false);
  const [keuze, setKeuze] = useState(null); // mail waarvoor het account/contactpersoon-keuzevenster open is
  const bccAdres = 'sales@fullframe.nl';

  const teWijzen = log.filter((e) => !e.prospect_id).sort((a, b) => new Date(b.datum) - new Date(a.datum));
  const toegewezen = log.filter((e) => e.prospect_id);
  const gefilterd = toegewezen.filter((e) => (e.naam + (e.bedrijf || '') + (e.email || '')).toLowerCase().includes(zoek.toLowerCase()))
    .sort((a, b) => new Date(b.datum) - new Date(a.datum));

  const verwijder = async (id) => {
    if (!(await bevestig({ titel: 'Mail uit het logboek verwijderen?', tekst: 'Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try { await api.delMailLog(id); reload(); } catch {}
  };
  const koppel = async (mailId, prospectId) => {
    if (!prospectId) return;
    setBusy(true);
    try { await api.assignMailLog(mailId, prospectId); await reload(); } catch {} finally { setBusy(false); }
  };
  const richtingBadge = (e) => (
    <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 20, padding: '1px 7px', whiteSpace: 'nowrap', background: e.richting === 'inkomend' ? '#d5e3fd' : '#f1f4f5', color: e.richting === 'inkomend' ? '#526075' : '#767c7e' }}>
      {e.richting === 'inkomend' ? '↓ Inkomend' : '↑ Uitgaand'}
    </span>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>Mail-log</div>
          <div style={{ fontSize: 14, color: '#556071', marginTop: 2 }}>{log.length} mails · BCC: {bccAdres}</div>
        </div>
      </div>

      {/* Toe te wijzen — inkomende/ongematchte mails zonder prospect */}
      {teWijzen.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <Icon name="alert" size={14} color="#D97706" />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#D97706' }}>Toe te wijzen ({teWijzen.length})</span>
          </div>
          {teWijzen.map((e) => (
            <div key={e.id} style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 14, padding: '12px 14px', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                {richtingBadge(e)}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{e.naam || e.email || 'Onbekend'}{e.bedrijf && <span style={{ fontWeight: 400, color: '#556071' }}> · {e.bedrijf}</span>}</div>
                  <div style={{ fontSize: 12, color: '#767c7e', marginTop: 1 }}>{e.email} · {fmtDt(e.datum)}</div>
                  {e.onderwerp && <div style={{ fontSize: 12, color: '#556071', marginTop: 1, fontStyle: 'italic' }}>"{e.onderwerp}"</div>}
                </div>
              </div>
              {e.email && prospects.filter((p) => (p.email || '').toLowerCase() === e.email.toLowerCase()).length > 1 && (
                <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: '#D97706' }}>
                  ⚠️ Meerdere prospects met dit e-mailadres — kies hieronder handmatig welke.
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <select disabled={busy} defaultValue="" onChange={(ev) => koppel(e.id, ev.target.value)}
                  style={{ fontFamily: 'inherit', border: '1px solid #e5e9eb', borderRadius: 8, padding: '7px 10px', fontSize: 13, background: '#fff', color: '#2d3335', flex: 1, minWidth: 180 }}>
                  <option value="" disabled>Koppel aan contactpersoon — kies account…</option>
                  {[...accounts].sort((a, b) => (a.naam || '').localeCompare(b.naam || '')).map((a) => {
                    const cps = prospects.filter((p) => p.account_id === a.id).sort((x, y) => (x.naam || '').localeCompare(y.naam || ''));
                    if (cps.length === 0) return null;
                    return (
                      <optgroup key={a.id} label={a.naam}>
                        {cps.map((p) => <option key={p.id} value={p.id}>{p.naam}{p.functie ? ` (${p.functie})` : ''}</option>)}
                      </optgroup>
                    );
                  })}
                </select>
                <button disabled={busy} onClick={() => setKeuze(e)}
                  style={{ cursor: busy ? 'default' : 'pointer', background: 'linear-gradient(135deg, #635983 0%, #526075 100%)', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 700, fontFamily: '"Manrope", system-ui, sans-serif', display: 'flex', alignItems: 'center', gap: 5, opacity: busy ? 0.7 : 1 }}>
                  <Icon name="plus" size={13} color="#fff" /> Nieuw…
                </button>
                <button title="Logregel verwijderen" disabled={busy} onClick={() => verwijder(e.id)}
                  style={{ cursor: 'pointer', background: '#FEF2F2', border: 'none', borderRadius: 8, padding: '8px 10px', display: 'flex', alignItems: 'center' }}>
                  <Icon name="trash" size={13} color="#EF4444" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ position: 'relative', marginBottom: 16 }}>
        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#767c7e', pointerEvents: 'none' }}>
          <Icon name="search" size={15} />
        </span>
        <input style={{ fontFamily: 'inherit', border: '1px solid #e5e9eb', borderRadius: 10, padding: '9px 12px 9px 36px', fontSize: 14, background: '#fff', color: '#2d3335', width: '100%', outline: 'none', boxSizing: 'border-box' }} placeholder="Zoek in toegewezen mails…" value={zoek} onChange={(e) => setZoek(e.target.value)} />
      </div>
      {gefilterd.length === 0
        ? <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e5e9eb', padding: '48px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📬</div>
            <div style={{ fontSize: 15, fontWeight: 500, color: '#556071' }}>{log.length === 0 ? 'Nog geen mails' : (toegewezen.length === 0 ? 'Nog geen toegewezen mails' : 'Niets gevonden')}</div>
          </div>
        : gefilterd.map((e) => (
          <div key={e.id} style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e9eb', overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', cursor: 'pointer' }} onClick={() => setOpen(open === e.id ? null : e.id)}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: '#d5e3fd', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name="mail" size={16} color="#526075" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>{e.naam} <span style={{ fontWeight: 400, color: '#556071' }}>· {e.bedrijf}</span> {richtingBadge(e)}</div>
                <div style={{ fontSize: 12, color: '#767c7e', marginTop: 2 }}>{e.email} · via {e.afzender} · {fmtDt(e.datum)}</div>
                {e.onderwerp && <div style={{ fontSize: 12, color: '#556071', marginTop: 1, fontStyle: 'italic' }}>"{e.onderwerp}"</div>}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button title="Logregel verwijderen" onClick={(ev) => { ev.stopPropagation(); verwijder(e.id); }} style={{ cursor: 'pointer', background: '#FEF2F2', border: 'none', borderRadius: 8, padding: '6px 8px', display: 'flex', alignItems: 'center' }}>
                  <Icon name="trash" size={13} color="#EF4444" />
                </button>
                <Icon name="chevron" size={14} color="#767c7e" />
              </div>
            </div>
            {open === e.id && (
              <div style={{ borderTop: '1px solid #f1f4f5', padding: 16, background: '#f1f4f5' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 12, color: '#767c7e' }}>Verkeerd gesorteerd? Verplaats naar:</span>
                  <select disabled={busy} value="" onChange={(ev) => { if (ev.target.value) koppel(e.id, ev.target.value); ev.target.value = ''; }}
                    style={{ fontFamily: 'inherit', border: '1px solid #e5e9eb', borderRadius: 8, padding: '6px 10px', fontSize: 13, background: '#fff', color: '#2d3335', flex: 1, minWidth: 200 }}>
                    <option value="" disabled>Contactpersoon — kies account…</option>
                    {[...accounts].sort((a, b) => (a.naam || '').localeCompare(b.naam || '')).map((a) => {
                      const cps = prospects.filter((p) => p.account_id === a.id && p.id !== e.prospect_id).sort((x, y) => (x.naam || '').localeCompare(y.naam || ''));
                      if (cps.length === 0) return null;
                      return (
                        <optgroup key={a.id} label={a.naam}>
                          {cps.map((p) => <option key={p.id} value={p.id}>{p.naam}{p.functie ? ` (${p.functie})` : ''}</option>)}
                        </optgroup>
                      );
                    })}
                  </select>
                  <button disabled={busy} onClick={() => setKeuze(e)}
                    style={{ cursor: busy ? 'default' : 'pointer', background: 'linear-gradient(135deg, #635983 0%, #526075 100%)', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 700, fontFamily: '"Manrope", system-ui, sans-serif', display: 'flex', alignItems: 'center', gap: 5, opacity: busy ? 0.7 : 1 }}>
                    <Icon name="plus" size={13} color="#fff" /> Nieuw…
                  </button>
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.75, color: '#556071', whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{e.mail_tekst}</div>
              </div>
            )}
          </div>
        ))
      }

      {keuze && (
        <NieuwContactKeuzeModal
          mail={keuze}
          accounts={accounts}
          onClose={() => setKeuze(null)}
          onDone={reload}
        />
      )}
    </div>
  );
}

// ─── NieuwContactKeuzeModal (mail → nieuw of bestaand account) ────────
// Keuzevenster (stijl: twee selecteerbare kaarten) voor het koppelen van een
// toe-te-wijzen mail: maak een NIEUW account óf voeg een contactpersoon toe aan
// een BESTAAND account (met account-selector). Daarna wordt de mail gekoppeld.

function NieuwContactKeuzeModal({ mail, accounts = [], onClose, onDone }) {
  const [mode, setMode] = useState(accounts.length ? 'account' : 'account'); // 'account' | 'contact'
  const sortedAccounts = [...accounts].sort((a, b) => (a.naam || '').localeCompare(b.naam || ''));
  const [accountId, setAccountId] = useState(sortedAccounts[0]?.id || '');
  const [accountNaam, setAccountNaam] = useState((mail.bedrijf || mail.naam || mail.email || '').trim());
  const [naam, setNaam] = useState((mail.naam || mail.email || '').trim());
  const [email, setEmail] = useState(mail.email || '');
  const [busy, setBusy] = useState(false);

  const kan = naam.trim() && (mode === 'account' ? accountNaam.trim() : accountId);

  const submit = async () => {
    if (!kan) return;
    setBusy(true);
    try {
      let accId = accountId;
      if (mode === 'account') {
        const an = accountNaam.trim();
        accId = accounts.find((a) => (a.naam || '').toLowerCase() === an.toLowerCase())?.id || null;
        if (!accId) {
          const ar = await api.createAccount({ naam: an, eigenaar: 'Vincent' });
          if (ar.ok) { const { account } = await ar.json(); accId = account?.id; }
          else if (ar.status === 409) { const b = await ar.json().catch(() => ({})); accId = b.existingId; }
        }
      }
      if (!accId) { setBusy(false); return; }
      const res = await api.create({ naam: naam.trim(), email: email || '', account_id: accId, stage: 'nieuw' });
      let pid = null;
      if (res.ok) { const { prospect } = await res.json(); pid = prospect?.id; }
      else if (res.status === 409) { const b = await res.json().catch(() => ({})); pid = b.existingId; }
      if (pid) await api.assignMailLog(mail.id, pid);
      await onDone();
      onClose();
    } catch { /* stil — gebruiker kan opnieuw */ } finally { setBusy(false); }
  };

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(45,51,53,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(4px)' };
  const box = { background: '#fff', borderRadius: 18, padding: 28, width: 520, maxWidth: '94vw', boxShadow: '0 20px 60px rgba(45,51,53,0.18)', maxHeight: '92vh', overflowY: 'auto', fontFamily: 'Inter,-apple-system,BlinkMacSystemFont,sans-serif', color: '#2d3335' };
  const inp = { fontFamily: 'inherit', border: '1px solid #e5e9eb', borderRadius: 9, padding: '9px 12px', fontSize: 14, background: '#fff', color: '#2d3335', width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { fontSize: 11, fontWeight: 700, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6, display: 'block' };
  const card = (active) => ({ cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'flex-start', border: active ? '2px solid #526075' : '1px solid #e5e9eb', background: active ? '#eef2fb' : '#fff', borderRadius: 14, padding: '14px 16px', marginBottom: 10 });
  const iconBox = (active) => ({ width: 38, height: 38, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: active ? '#526075' : '#f1f4f5' });

  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>Contactpersoon toevoegen</div>
        <div style={{ fontSize: 14, color: '#556071', marginBottom: 18, lineHeight: 1.5 }}>Koppel deze mail aan een nieuw account of aan een bestaand account.</div>

        <div onClick={() => setMode('account')} style={card(mode === 'account')}>
          <div style={iconBox(mode === 'account')}><Icon name="plus" size={18} color={mode === 'account' ? '#fff' : '#767c7e'} /></div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: mode === 'account' ? '#526075' : '#2d3335' }}>Nieuw account</div>
            <div style={{ fontSize: 13, color: '#556071', marginTop: 2, lineHeight: 1.45 }}>Maak een nieuw account aan, met deze contactpersoon erin.</div>
          </div>
        </div>
        <div onClick={() => accounts.length && setMode('contact')} style={{ ...card(mode === 'contact'), opacity: accounts.length ? 1 : 0.5, cursor: accounts.length ? 'pointer' : 'not-allowed' }}>
          <div style={iconBox(mode === 'contact')}><Icon name="user" size={18} color={mode === 'contact' ? '#fff' : '#767c7e'} /></div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: mode === 'contact' ? '#526075' : '#2d3335' }}>Bestaand account</div>
            <div style={{ fontSize: 13, color: '#556071', marginTop: 2, lineHeight: 1.45 }}>Voeg deze contactpersoon toe aan een bestaand account.</div>
          </div>
        </div>

        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {mode === 'account' ? (
            <div><label style={lbl}>Accountnaam *</label><input style={inp} value={accountNaam} onChange={(e) => setAccountNaam(e.target.value)} placeholder="Bureau / bedrijf" /></div>
          ) : (
            <div><label style={lbl}>Account *</label>
              <select style={inp} value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                <option value="" disabled>Kies account…</option>
                {sortedAccounts.map((a) => <option key={a.id} value={a.id}>{a.naam}</option>)}
              </select>
            </div>
          )}
          <div><label style={lbl}>Naam contactpersoon *</label><input style={inp} value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="Voor- en achternaam" /></div>
          <div><label style={lbl}>E-mail</label><input style={inp} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="naam@bedrijf.nl" /></div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 22, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ cursor: 'pointer', background: '#f1f4f5', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', color: '#556071' }}>Annuleren</button>
          <button onClick={submit} disabled={!kan || busy} style={{ cursor: (!kan || busy) ? 'not-allowed' : 'pointer', background: 'linear-gradient(135deg, #635983 0%, #526075 100%)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 14, fontWeight: 700, fontFamily: '"Manrope", system-ui, sans-serif', opacity: (!kan || busy) ? 0.55 : 1 }}>{busy ? 'Bezig…' : 'Aanmaken & koppelen'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── WeekTracker ─────────────────────────────────────────────────────

function WeekTracker({ afspraken }) {
  const tw = getWeekKey(), tc = afspraken[tw] || 0, bereikt = tc >= 1;
  const weeks = getLast8Weeks(), tot = weeks.reduce((s, w) => s + (afspraken[w] || 0), 0);
  const beh = weeks.filter((w) => (afspraken[w] || 0) >= 1).length;
  const bMax = Math.max(...weeks.map((w) => afspraken[w] || 0), 1);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('salesWeekTrackerCollapsed') === '1'; } catch { return false; }
  });
  const toggle = () => setCollapsed((c) => { const n = !c; try { localStorage.setItem('salesWeekTrackerCollapsed', n ? '1' : '0'); } catch { /* ignore */ } return n; });
  return (
    <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e5e9eb', padding: collapsed ? '12px 20px' : '20px', marginBottom: 20 }}>
      <div onClick={toggle} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: collapsed ? 0 : 14, cursor: 'pointer', userSelect: 'none' }}>
        <Icon name="target" size={13} color="#526075" />
        <span style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Doel: 1 afspraak per week</span>
        {collapsed && (
          <span style={{ fontSize: 11, fontWeight: 600, color: bereikt ? '#10B981' : '#556071', marginLeft: 4 }}>
            · deze week: {tc} {bereikt ? '✓' : ''}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <Icon name="chevron" size={14} color="#adb3b5" style={{ transform: collapsed ? 'none' : 'rotate(90deg)' }} />
      </div>
      {!collapsed && (
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: '#556071', marginBottom: 10 }}>{getWeekLabel(tw)}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ textAlign: 'center', minWidth: 60 }}>
              <div style={{ fontSize: 44, fontWeight: 800, lineHeight: 1, color: bereikt ? '#10B981' : '#2d3335' }}>{tc}</div>
              <div style={{ fontSize: 11, color: '#767c7e', marginTop: 2 }}>deze week</div>
            </div>
          </div>
          <div style={{ width: 160, height: 6, background: '#f1f4f5', borderRadius: 99, overflow: 'hidden', marginBottom: 6 }}>
            <div style={{ height: '100%', width: bereikt ? '100%' : '0%', background: '#10B981', borderRadius: 99, transition: 'width 0.4s' }} />
          </div>
          <div style={{ fontSize: 12, color: bereikt ? '#10B981' : '#556071', fontWeight: bereikt ? 600 : 400 }}>{bereikt ? 'Doel bereikt!' : 'Nog 1 afspraak te gaan'}</div>
          <div style={{ fontSize: 11, color: '#767c7e', marginTop: 6, lineHeight: 1.5, maxWidth: 180 }}>Telt automatisch mee zodra je een contactmoment logt met "afspraak gemaakt".</div>
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Laatste 8 weken</span>
            <span style={{ fontSize: 12, color: '#556071' }}>{beh}/8 op doel</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 70 }}>
            {weeks.map((w) => {
              const n = afspraken[w] || 0, cur = w === tw, hit = n >= 1, pct = (n / bMax) * 100;
              return (
                <div key={w} title={getWeekLabel(w) + ': ' + n} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', gap: 2 }}>
                  {n > 0 && <span style={{ fontSize: 9, fontWeight: 700, color: hit ? '#10B981' : '#526075' }}>{n}</span>}
                  <div style={{ width: '100%', minHeight: 4, height: Math.max(pct, 6) + '%', borderRadius: '4px 4px 0 0', background: hit ? '#10B981' : cur ? '#526075' : '#adb3b5', opacity: cur ? 1 : hit ? 0.85 : 0.4, outline: cur ? '1.5px solid #465469' : 'none', transition: 'height 0.3s' }} />
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            {weeks.map((w) => <div key={w} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: w === tw ? '#526075' : '#767c7e', fontWeight: w === tw ? 700 : 400 }}>W{w.split('-W')[1]}</div>)}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
          {[{ l: 'Totaal', v: tot, c: '#526075' }, { l: 'Op doel', v: `${beh}/8`, c: '#10B981' }].map(({ l, v, c }) => (
            <div key={l} style={{ background: '#f1f4f5', borderRadius: 12, padding: '10px 18px', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: c }}>{v}</div>
              <div style={{ fontSize: 11, color: '#767c7e', marginTop: 2 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}

// ─── StatsDashboard ──────────────────────────────────────────────────

function StatsDashboard({ prospects, accounts = [], contacts, mailLog, afspraken, team, isMobile }) {
  const eigenaarVan = (k) => accounts.find((a) => a.id === k.account_id)?.eigenaar || null;
  const [periode, setPeriode] = useState('maand');
  const now = new Date(), wekKey = getWeekKey(), weeks8 = getLast8Weeks();
  const mndKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const mailsInPeriode = mailLog.filter((e) => {
    const d = new Date(e.datum);
    return periode === 'week' ? getWeekKey(d) === wekKey : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` === mndKey;
  });
  const afsprakenLog = contacts.filter((c) => c.afspraak_gemaakt && c.datum);
  const afsprakenInPeriode = afsprakenLog.filter((c) => {
    const d = new Date(c.datum);
    return periode === 'week' ? getWeekKey(d) === wekKey : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` === mndKey;
  }).length;
  const totaal = prospects.length;
  const klantCount = prospects.filter((k) => k.stage === 'klant').length;
  const convPct = totaal > 0 ? Math.round(klantCount / totaal * 100) : 0;
  const verstuurd = prospects.filter((k) => k.mail_verstuurd).length;
  const metRespons = prospects.filter((k) => k.respons === 'ja').length;
  const negatief = prospects.filter((k) => k.respons === 'nee').length;
  const responsPct = verstuurd > 0 ? Math.round(metRespons / verstuurd * 100) : 0;
  const afsprakenUitLog = contacts.filter((c) => c.afspraak_gemaakt).length;
  const perStage = STAGES.map((s) => ({ ...s, n: prospects.filter((k) => k.stage === s.id).length }));
  const kanaalUitLog = KANAAL_OPTIES.map((k) => {
    const m = contacts.filter((c) => c.kanaal === k);
    const a = m.filter((c) => c.afspraak_gemaakt).length;
    return { kanaal: k, totaal: m.length, afspraken: a, pct: m.length > 0 ? Math.round(a / m.length * 100) : 0 };
  }).filter((k) => k.totaal > 0).sort((a, b) => b.pct - a.pct);
  const eigenaarStats = team.map((e) => {
    const ek = prospects.filter((k) => eigenaarVan(k) === e);
    return { naam: e, totaal: ek.length, klanten: ek.filter((k) => k.stage === 'klant').length, respons: ek.filter((k) => k.respons === 'ja').length };
  }).filter((e) => e.totaal > 0);
  const teamTotaal = eigenaarStats.reduce((s, e) => s + e.totaal, 0);
  const teamKlanten = eigenaarStats.reduce((s, e) => s + e.klanten, 0);
  const weekTrend = weeks8.map((w) => ({
    label: 'W' + w.split('-W')[1],
    afspraken: afspraken[w] || 0,
    mails: mailLog.filter((e) => getWeekKey(new Date(e.datum)) === w).length,
  }));
  const trendMax = Math.max(...weekTrend.map((w) => Math.max(w.afspraken, w.mails)), 1);
  const card = { background: '#fff', borderRadius: 16, border: '1px solid #e5e9eb', padding: '18px 20px' };
  const lbl = { fontSize: 11, fontWeight: 600, color: '#767c7e', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4, display: 'block' };
  const Bar = ({ pct, color, h = 7 }) => (
    <div style={{ background: '#f1f4f5', borderRadius: 99, height: h, overflow: 'hidden', flex: 1 }}>
      <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 99, transition: 'width 0.5s' }} />
    </div>
  );
  const SC = ({ label, value, color, sub, icon }) => (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={lbl}>{label}</span>
        <Icon name={icon} size={14} color="#adb3b5" />
      </div>
      <div style={{ fontSize: 36, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 13, color: '#556071', marginTop: 4 }}>{sub}</div>}
    </div>
  );
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>Statistieken</div>
          <div style={{ fontSize: 14, color: '#556071', marginTop: 2 }}>{periode === 'week' ? getWeekLabel(wekKey) : now.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}</div>
        </div>
        <div style={{ display: 'flex', background: '#f1f4f5', borderRadius: 10, padding: 3, gap: 2 }}>
          {[{ id: 'week', l: 'Deze week' }, { id: 'maand', l: 'Deze maand' }].map(({ id, l }) => (
            <button key={id} onClick={() => setPeriode(id)} style={{ cursor: 'pointer', padding: '6px 14px', borderRadius: 8, border: 'none', fontFamily: 'inherit', fontSize: 13, fontWeight: periode === id ? 600 : 400, background: periode === id ? '#fff' : 'transparent', color: periode === id ? '#2d3335' : '#556071', boxShadow: periode === id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>{l}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(155px,1fr))', gap: 12, marginBottom: 16 }}>
        <SC label="Totaal prospects" value={totaal} color="#2d3335" icon="user" sub={`${klantCount} klant geworden`} />
        <SC label="Conversie" value={convPct + '%'} color="#F59E0B" icon="trend" sub={`${klantCount} van ${totaal}`} />
        <SC label="Positieve respons" value={responsPct + '%'} color="#526075" icon="mail" sub={`${metRespons} van ${verstuurd}`} />
        <SC label="Afspraken" value={afsprakenInPeriode} color="#10B981" icon="target" sub={periode === 'week' ? 'deze week' : 'deze maand'} />
        <SC label="Mails verstuurd" value={mailsInPeriode.length} color="#635983" icon="send" sub={periode === 'week' ? 'deze week' : 'deze maand'} />
        <SC label="Afspraken totaal" value={afsprakenUitLog} color="#10B981" icon="calcheck" sub="uit contactlog" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div style={card}>
          <span style={lbl}>Conversie funnel</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
            {perStage.map((s) => (
              <div key={s.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: '#556071', fontWeight: 500 }}>{s.label}</span>
                  <span style={{ fontSize: 12, color: s.accent, fontWeight: 700 }}>{s.n}</span>
                </div>
                <Bar pct={totaal > 0 ? (s.n / totaal) * 100 : 0} color={s.accent} />
              </div>
            ))}
          </div>
        </div>
        <div style={card}>
          <span style={lbl}>Trend laatste 8 weken</span>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, marginTop: 6 }}>
            {[{ k: '#526075', l: 'Mails' }, { k: '#10B981', l: 'Afspraken' }].map(({ k, l }) => (
              <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#556071' }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, background: k }} />{l}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 80 }}>
            {weekTrend.map((w, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, height: '100%', justifyContent: 'flex-end' }}>
                <div style={{ width: '100%', display: 'flex', gap: 1, alignItems: 'flex-end', height: '100%', justifyContent: 'center' }}>
                  <div style={{ flex: 1, background: '#526075', opacity: 0.7, borderRadius: '3px 3px 0 0', height: `${Math.max(w.mails / trendMax * 100, w.mails > 0 ? 6 : 0)}%`, minHeight: w.mails > 0 ? 4 : 0 }} />
                  <div style={{ flex: 1, background: '#10B981', borderRadius: '3px 3px 0 0', height: `${Math.max(w.afspraken / trendMax * 100, w.afspraken > 0 ? 6 : 0)}%`, minHeight: w.afspraken > 0 ? 4 : 0 }} />
                </div>
                <div style={{ fontSize: 9, color: w.label === ('W' + wekKey.split('-W')[1]) ? '#526075' : '#767c7e', fontWeight: w.label === ('W' + wekKey.split('-W')[1]) ? 700 : 400 }}>{w.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}><Icon name="bar" size={13} color="#526075" /><span style={lbl}>Kanaal (uit contactlog)</span></div>
          {kanaalUitLog.length === 0
            ? <div style={{ fontSize: 13, color: '#767c7e' }}>Log contactmomenten per prospect om dit te vullen.</div>
            : kanaalUitLog.map((k, i) => (
              <div key={k.kanaal} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: '#556071' }}>{i === 0 ? '🏆 ' : ''}{k.kanaal}</span>
                  <span style={{ fontSize: 11, color: '#556071' }}>{k.afspraken}/{k.totaal} ({k.pct}%)</span>
                </div>
                <Bar pct={k.pct} color={i === 0 ? '#526075' : '#d5e3fd'} />
              </div>
            ))
          }
        </div>
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}><Icon name="user" size={13} color="#526075" /><span style={lbl}>Werkverdeling team</span></div>
          {eigenaarStats.length === 0
            ? <div style={{ fontSize: 13, color: '#767c7e' }}>Ken prospects toe aan teamleden.</div>
            : <>
              <div style={{ fontSize: 13, color: '#556071', marginBottom: 14, lineHeight: 1.5 }}>
                Samen goed voor <strong style={{ color: '#2d3335' }}>{teamKlanten}</strong> {teamKlanten === 1 ? 'klant' : 'klanten'} uit <strong style={{ color: '#2d3335' }}>{teamTotaal}</strong> prospects.
              </div>
              {eigenaarStats.map((e) => (
                <div key={e.naam} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#556071' }}>{e.naam}</span>
                    <span style={{ fontSize: 11, color: '#556071' }}>{e.totaal} {e.totaal === 1 ? 'prospect' : 'prospects'}{e.klanten > 0 ? ` · ${e.klanten} klant` : ''}</span>
                  </div>
                  <Bar pct={teamTotaal > 0 ? (e.totaal / teamTotaal) * 100 : 0} color="#d5e3fd" />
                </div>
              ))}
            </>
          }
        </div>
      </div>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}><Icon name="mail" size={13} color="#526075" /><span style={lbl}>Mail respons overzicht</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10 }}>
          {[{ l: 'Verstuurd', v: verstuurd, c: '#526075', bg: '#d5e3fd' }, { l: 'Positief', v: metRespons, c: '#10B981', bg: '#ECFDF5' }, { l: 'Negatief', v: negatief, c: '#EF4444', bg: '#FEF2F2' }, { l: 'Geen respons', v: Math.max(0, verstuurd - metRespons - negatief), c: '#767c7e', bg: '#f1f4f5' }].map(({ l, v, c, bg }) => (
            <div key={l} style={{ background: bg, borderRadius: 12, padding: '12px 14px' }}>
              <div style={{ fontSize: 24, fontWeight: 800, color: c }}>{v}</div>
              <div style={{ fontSize: 11, color: '#556071', marginTop: 3 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
